import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import useAppTheme from 'hooks/useAppTheme';
import EventSourcePolyfillModule from 'event-source-polyfill';
import DispatchMonitorAPI, {
  type HaulerAssignmentChangePayload,
} from 'api/dispatch-monitor.api';
import ManualDispatchAPI, {
  type ManualDispatchAssignment,
  type ManualDispatchExcavatorConfig,
  type ManualDispatchTruckCommitInput,
  type ManualDispatchTruckDivert,
} from 'api/manual-dispatch.api';
import useDispatchMode from 'hooks/useDispatchMode';
import { useSortableRows, type SortValueGetters } from 'hooks/useSortableRows';
import { SortableTh } from 'components/Table/SortableTh';
import { SseLogsViewer, type SseLogEntry } from 'components/Logs/SseLogsViewer';
import AssetAPI from 'api/asset.api';
import LocationImporterAPI, {
  type ImportedLocation,
} from 'api/location-importer.api';
import GradeImporterAPI from 'api/grade-importer.api';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';
import MaterialGradeAPI from 'api/material-grade.api';
import { type MaterialGradeRecord } from 'views/DispatchImporters/MaterialGradeTonnage/types';
import StatusCodesAPI from 'api/status-codes.api';
import { type StatusCodeRecord } from 'views/Settings/StatusCodes/types';
import { selectAssociatedAssets } from 'store/asset.slice';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  buildLoadingLocationExcavatorMap,
  getAssignmentDisplayName as getAssignmentDisplayNameShared,
} from 'utils/loadingDisplay';
import {
  isInternalRole,
  isInternalUser,
} from 'utils/validators/role.validation';

type EventSourcePolyfillType = new (
  url: string,
  init?: { headers?: Record<string, string> }
) => EventSource;

const EventSourcePolyfill = (EventSourcePolyfillModule as any)
  .EventSourcePolyfill as EventSourcePolyfillType;

// ─── Types ───────────────────────────────────────────────────────────────────

interface MakeModelDetails {
  make: string;
  model: string;
}

interface OperatorInfo {
  operatorName: string;
}

interface AssetInfo {
  bumperNumber: string;
  symbotId: string;
  assetType: string;
  meta: Record<string, unknown> | null;
  makeModelDetails: MakeModelDetails | null;
  operator: OperatorInfo | null;
  lat: number;
  lon: number;
  nearestLoading: { locationId: string; name: string } | null;
}

interface LastStatus {
  payload: { status: string; reason: string; description?: string } | null;
  operatorName: string | null;
  operatorLoggedOut?: boolean;
}

const MAX_SSE_LOGS = 300;

// stable internal values (never locale-dependent) — display labels come from
// the matching hm_*_assignment i18n keys
const ASSIGNMENT_TYPE_OPTIONS: Array<{
  value: 'manual' | 'shovel' | 'locked';
  labelKey: string;
}> = [
  { value: 'manual', labelKey: 'hm_manual_assignment' },
  { value: 'shovel', labelKey: 'hm_shovel_assignment' },
  { value: 'locked', labelKey: 'hm_locked_assignment' },
];

interface TruckEdit {
  activity: string;
  shovelLock: string;
  regionLock: string;
  currentAssignment: string;
  assignmentType: string;
  nextAssignment: string;
}

interface LiveTelemetry {
  currentAssignment?: string;
  nextAssignment?: string;
}

// TruckEdit fields that require an explicit Save to persist to the backend
const PERSISTED_FIELDS = new Set<keyof TruckEdit>([
  'shovelLock',
  'regionLock',
  'currentAssignment',
  'nextAssignment',
]);

// manual mode only — sessionStorage key (per customer) Save writes staged
// edits to, so they survive a refresh before Override Plan commits them
const MANUAL_STAGED_EDITS_KEY_PREFIX = 'hm_manual_staged_edits_';

const PARKING_LOCATION_TYPES = new Set(['parking']);
const LOADING_LOCATION_TYPES = new Set(['loading']);
const FUELING_LOCATION_TYPES = new Set(['fueling']);
const DUMP_LOCATION_TYPES = new Set(['dumping', 'stockpile', 'crushing']);
const CRUSHER_LOCATION_TYPES = new Set(['crusher']);
// a stockpile is a detour like parking/fueling (it can sit on either side of
// the load↔dump cycle), unlike a plain dump/crusher — used alongside
// PARKING_LOCATION_TYPES/FUELING_LOCATION_TYPES below wherever that
// distinction matters
const STOCKPILE_LOCATION_TYPES = new Set(['stockpile']);

// Activity-based filtering for Current/Next Assignment options — a truck
// hauling loaded material can only be headed to a dump-type/parking/fuel
// node next (never another loading point), and vice versa while traveling
// empty back to load. LOADING/DUMPING (actively at that node) narrow current
// to matching-type locations, with next following the hauling/traveling rule
// for whichever leg comes after.
const DUMP_LIKE_OR_PARK_OR_FUEL_TYPES = new Set([
  ...DUMP_LOCATION_TYPES,
  ...CRUSHER_LOCATION_TYPES,
  ...PARKING_LOCATION_TYPES,
  ...FUELING_LOCATION_TYPES,
]);
const LOAD_OR_PARK_OR_FUEL_TYPES = new Set([
  ...LOADING_LOCATION_TYPES,
  ...PARKING_LOCATION_TYPES,
  ...FUELING_LOCATION_TYPES,
]);

const CURRENT_ASSIGNMENT_TYPES_BY_ACTIVITY: Record<string, Set<string>> = {
  hauling: DUMP_LIKE_OR_PARK_OR_FUEL_TYPES,
  traveling: LOAD_OR_PARK_OR_FUEL_TYPES,
  loading: LOADING_LOCATION_TYPES,
  dumping: DUMP_LIKE_OR_PARK_OR_FUEL_TYPES,
};
const NEXT_ASSIGNMENT_TYPES_BY_ACTIVITY: Record<string, Set<string>> = {
  hauling: LOAD_OR_PARK_OR_FUEL_TYPES,
  traveling: DUMP_LIKE_OR_PARK_OR_FUEL_TYPES,
  loading: DUMP_LIKE_OR_PARK_OR_FUEL_TYPES,
  dumping: LOAD_OR_PARK_OR_FUEL_TYPES,
};

const resolveNodeType = (
  locationType: string
): 'loading' | 'dumping' | 'parking' => {
  const lt = locationType?.trim()?.toLowerCase() ?? '';
  if (LOADING_LOCATION_TYPES.has(lt)) return 'loading';
  // matches backend's isParkingOrFuelingLocationType (manual-dispatch.service.ts) —
  // a fueling stop must resolve to 'parking' (parkingLocationId), never 'dumping'.
  // Sent as 'dumping', it skips the backend's one-off-divert handling entirely and
  // gets folded into the excavator's loading/dumping route pairs instead, silently
  // overwriting its current assignment.
  if (PARKING_LOCATION_TYPES.has(lt) || /fuel/i.test(lt)) return 'parking';
  return 'dumping';
};

// Raw status values that display as "READY" — activity/reason text is only
// meaningful (and only shown) while an asset is in one of these states.
const isReadyStatus = (statusRaw?: string | null): boolean => {
  const rawKey = statusRaw?.toLowerCase() ?? '';
  return rawKey === 'active' || rawKey === 'ready';
};

const STATUS_COLORS: Record<string, string> = {
  active: '#4caf50',
  ready: '#4caf50',
  standby: '#3d5a99',
  delay: '#8b6914',
  down: '#8b2e2e',
};

// ─── Inner component ─────────────────────────────────────────────────────────

const HaulersMonitorInner: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enableDarkTheme } = useAppTheme();
  const dispatch = useAppDispatch();
  const storeAssets = useAppSelector(selectAssociatedAssets);
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const { customerCode } = useCustomerInfo();
  const role = useAppSelector((s) => s.authReducer.role);
  const canViewSseLogs =
    isInternalRole(role ?? 'internal-user') || isInternalUser(role);

  const dispatchApi = useRef(new DispatchMonitorAPI()).current;
  const assetApi = useRef(new AssetAPI()).current;
  const locationApi = useRef(new LocationImporterAPI()).current;
  const gradeApi = useRef(new GradeImporterAPI()).current;
  const materialGradeApi = useRef(new MaterialGradeAPI()).current;
  const statusCodesApi = useRef(new StatusCodesAPI()).current;
  const manualDispatchApi = useRef(new ManualDispatchAPI()).current;
  const { mode } = useDispatchMode();

  const getAssetId = (symbotId: string) =>
    storeAssets.find((a) => a.device?.deviceId === symbotId)?.assetId ?? null;

  const [assets, setAssets] = useState<AssetInfo[]>([]);
  const [lastStatuses, setLastStatuses] = useState<Record<string, LastStatus>>(
    {}
  );
  const [allLocations, setAllLoadingLocations] = useState<ImportedLocation[]>(
    []
  );
  const activeLocations = useMemo(
    () => allLocations.filter((l) => l.active),
    [allLocations]
  );

  // Intersections are road waypoints, not valid loading/dumping/parking targets
  const assignableLocations = useMemo(
    () =>
      activeLocations.filter(
        (l) => (l.locationType?.trim()?.toLowerCase() ?? '') !== 'intersection'
      ),
    [activeLocations]
  );

  // Real-time activity (Hauling/Traveling/Loading/Dumping) narrows the option
  // pool first — a hauling truck can only be headed to a dump-type/parking/
  // fuel node, never another loading point, and vice versa. Falls back to
  // the full assignable list for any other/unknown activity (Ready, Standby,
  // etc.) rather than over-restricting when we don't have a clear signal.
  const activityFilteredLocations = (
    byActivity: Record<string, Set<string>>,
    activity?: string
  ): ImportedLocation[] => {
    const types = byActivity[activity?.trim()?.toLowerCase() ?? ''];
    if (!types) return assignableLocations;
    return assignableLocations.filter((l) =>
      types.has(l.locationType?.trim()?.toLowerCase() ?? '')
    );
  };

  // Given the location currently picked for "Current Assignment", narrow down
  // what makes sense to offer for "Next Assignment"
  const getNextAssignmentOptions = (
    currentAssignmentName?: string,
    activity?: string
  ) => {
    const pool = activityFilteredLocations(
      NEXT_ASSIGNMENT_TYPES_BY_ACTIVITY,
      activity
    );
    if (!currentAssignmentName) return pool;
    // looked up against the full list, not `pool` — current's own type-set is
    // generally different from next's, so it won't be found inside `pool`
    const currentLoc = assignableLocations.find(
      (l) => l.name === currentAssignmentName
    );
    if (!currentLoc) return pool;
    const currentType = currentLoc.locationType?.trim()?.toLowerCase() ?? '';
    const excludeSelf = (l: ImportedLocation) =>
      l.locationId !== currentLoc.locationId;

    // a parking/fuel/stockpile stop is a detour that can sit on either side
    // of the load↔dump cycle — the activity-based `pool` above assumes one
    // specific side (e.g. "traveling" only allows a dump-like next), which
    // wrongly excludes the real next leg when it's actually loading (refuel,
    // then straight back to load) — fall back to the full assignable list
    // rather than the activity-narrowed pool whenever current is one of these
    if (
      PARKING_LOCATION_TYPES.has(currentType) ||
      FUELING_LOCATION_TYPES.has(currentType) ||
      STOCKPILE_LOCATION_TYPES.has(currentType)
    ) {
      return assignableLocations.filter(excludeSelf);
    }

    // a crusher is a dump destination too — the truck's next stop after
    // dumping at a crusher is normally back to a loading point (the primary
    // load ↔ crusher cycle), so loading must stay in the allowed pool here
    // exactly like it does for the DUMP_LOCATION_TYPES branch below
    if (CRUSHER_LOCATION_TYPES.has(currentType)) {
      return pool.filter((l) => {
        const lt = l.locationType?.trim()?.toLowerCase() ?? '';
        return (
          excludeSelf(l) &&
          (LOADING_LOCATION_TYPES.has(lt) ||
            DUMP_LOCATION_TYPES.has(lt) ||
            PARKING_LOCATION_TYPES.has(lt) ||
            FUELING_LOCATION_TYPES.has(lt))
        );
      });
    }

    if (DUMP_LOCATION_TYPES.has(currentType)) {
      return pool.filter((l) => {
        const lt = l.locationType?.trim()?.toLowerCase() ?? '';
        return (
          excludeSelf(l) &&
          (LOADING_LOCATION_TYPES.has(lt) ||
            CRUSHER_LOCATION_TYPES.has(lt) ||
            PARKING_LOCATION_TYPES.has(lt) ||
            FUELING_LOCATION_TYPES.has(lt))
        );
      });
    }

    return pool.filter(excludeSelf);
  };

  // Mirror of getNextAssignmentOptions — given what's picked for "Next Assignment",
  // narrow down what makes sense to offer for "Current Assignment"
  const getCurrentAssignmentOptions = (
    nextAssignmentName?: string,
    activity?: string
  ) => {
    const pool = activityFilteredLocations(
      CURRENT_ASSIGNMENT_TYPES_BY_ACTIVITY,
      activity
    );
    if (!nextAssignmentName) return pool;
    // looked up against the full list, not `pool` — next's own type-set is
    // generally different from current's, so it won't be found inside `pool`
    const nextLoc = assignableLocations.find(
      (l) => l.name === nextAssignmentName
    );
    if (!nextLoc) return pool;
    const nextType = nextLoc.locationType?.trim()?.toLowerCase() ?? '';
    const excludeSelf = (l: ImportedLocation) =>
      l.locationId !== nextLoc.locationId;

    // mirror of the parking/fuel/stockpile fallback in
    // getNextAssignmentOptions — a fuel/park/stockpile stop as "next"
    // doesn't tell us which side of the load↔dump cycle we're on, so don't
    // let the activity-narrowed pool exclude the real current value
    if (
      PARKING_LOCATION_TYPES.has(nextType) ||
      FUELING_LOCATION_TYPES.has(nextType) ||
      STOCKPILE_LOCATION_TYPES.has(nextType)
    ) {
      return assignableLocations.filter(excludeSelf);
    }

    // next = loading makes sense after a dump-type OR crusher-type current
    // (a crusher is a dump destination too — the load ↔ crusher cycle is the
    // primary case here) — no type restriction needed beyond excluding self
    if (LOADING_LOCATION_TYPES.has(nextType)) {
      return pool.filter(excludeSelf);
    }

    // next = dump-type never follows another dump-type current
    if (DUMP_LOCATION_TYPES.has(nextType)) {
      return pool.filter((l) => {
        const lt = l.locationType?.trim()?.toLowerCase() ?? '';
        return excludeSelf(l) && !DUMP_LOCATION_TYPES.has(lt);
      });
    }

    return pool.filter(excludeSelf);
  };
  const [grades, setGrades] = useState<GradeRecord[]>([]);
  const [materialGrades, setMaterialGrades] = useState<MaterialGradeRecord[]>(
    []
  );
  const [sseTonnage, setSseTonnage] = useState<Record<string, number>>({});
  const [loadingPointMap, setLoadingPointMap] = useState<
    Record<string, string>
  >({});
  const [statusCodes, setStatusCodes] = useState<StatusCodeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  // SSE-driven, read-only — never written to by the dispatcher
  const [liveTelemetry, setLiveTelemetry] = useState<
    Record<string, LiveTelemetry>
  >({});
  // dispatcher-driven, staged until Override Plan commits them — a live SSE
  // update for currentAssignment/nextAssignment overrides a pending staged
  // edit on that same field the instant it arrives (see clearStagedFieldOnSse)
  const [stagedEdits, setStagedEdits] = useState<
    Record<string, Partial<TruckEdit>>
  >({});
  const [statusModal, setStatusModal] = useState<{
    truck: AssetInfo;
    status: string;
    reason: string;
    description: string;
  } | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);

  // bell tracking — only show when SSE pushes "down" after page load
  const initialDownAtLoad = useRef<Set<string>>(new Set());
  const [userSetDown, setUserSetDown] = useState<Set<string>>(new Set());
  const [confirmedDown, setConfirmedDown] = useState<Set<string>>(new Set());
  const [downConfirmModal, setDownConfirmModal] = useState<AssetInfo | null>(
    null
  );

  // dev/internal-only raw SSE log viewer
  const [sseLogs, setSseLogs] = useState<SseLogEntry[]>([]);
  const sseLogSeqRef = useRef(0);
  // messages can arrive far faster than React can usefully re-render — buffer
  // them here and flush to state at most a few times a second instead of once
  // per message, so a burst of SSE traffic can't freeze the whole table.
  const pendingSseLogsRef = useRef<SseLogEntry[]>([]);
  const sseLogFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // manual mode: fallback dump picker when locking a truck to an excavator with
  // no configured dump zone of its own to denormalize from (Backend Update #10 —
  // gradeId is always resolved server-side from excavatorBumper, never needed here)
  const [addAssignmentPicker, setAddAssignmentPicker] = useState<{
    symbotId: string;
    excavatorBumper: string;
    dumpingZoneId: string;
  } | null>(null);
  // more than one dirty truck in the same commit can need the fallback picker —
  // queue the rest instead of clobbering addAssignmentPicker, which would
  // silently drop every truck but the last one that needed it
  const [queuedAssignmentPickers, setQueuedAssignmentPickers] = useState<
    Array<{ symbotId: string; excavatorBumper: string; dumpingZoneId: string }>
  >([]);

  useEffect(() => {
    if (addAssignmentPicker === null && queuedAssignmentPickers.length > 0) {
      setAddAssignmentPicker(queuedAssignmentPickers[0]);
      setQueuedAssignmentPickers((prev) => prev.slice(1));
    }
  }, [addAssignmentPicker, queuedAssignmentPickers]);

  // symbotIds with edits to shovelLock/regionLock not yet persisted to the backend
  const [dirtyTrucks, setDirtyTrucks] = useState<Set<string>>(new Set());
  const [isSavingAll, setIsSavingAll] = useState(false);
  const hasDirty = dirtyTrucks.size > 0;

  // symbotIds saved but with an assignment change not yet sent to the dispatch override API
  const [pendingOverrideTrucks, setPendingOverrideTrucks] = useState<
    Set<string>
  >(new Set());
  const [isOverriding, setIsOverriding] = useState(false);
  const hasPendingOverride = pendingOverrideTrucks.size > 0;

  const [activeAssignments, setActiveAssignments] = useState<
    ManualDispatchAssignment[]
  >([]);
  // one row per excavator regardless of truck count — this is the excavator's
  // OWN configured grade/dump (set up on the excavator screen), available even
  // when it currently has zero locked trucks. This is what "Shovel Assignment"
  // should follow, as opposed to deriving from a sibling truck's assignment.
  const [excavatorConfigs, setExcavatorConfigs] = useState<
    ManualDispatchExcavatorConfig[]
  >([]);

  const refreshActivePlan = async () => {
    if (mode !== 'manual') return;
    const plan = await manualDispatchApi.getActivePlan();
    setActiveAssignments(plan?.assignments ?? []);
    setExcavatorConfigs(plan?.excavators ?? []);
  };

  useEffect(() => {
    void refreshActivePlan();
    const interval = setInterval(() => {
      void refreshActivePlan();
    }, 30000);
    return () => {
      clearInterval(interval);
    };
  }, [mode, manualDispatchApi]);

  // manual mode: staged edits persist to sessionStorage automatically (no more
  // explicit Save button) — hydrate any staged-but-uncommitted edits from a
  // previous visit this browser session, so a refresh or nav-away-and-back
  // doesn't lose them before Override Plan ever commits them (Backend Update #10)
  useEffect(() => {
    if (mode !== 'manual' || !customerId) return;
    try {
      const raw = sessionStorage.getItem(
        MANUAL_STAGED_EDITS_KEY_PREFIX + customerId
      );
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, Partial<TruckEdit>>;
      setStagedEdits((prev) => ({ ...parsed, ...prev }));
      setDirtyTrucks((prev) => {
        const next = new Set(prev);
        Object.entries(parsed).forEach(([symbotId, edit]) => {
          const touchedPersistedField = Array.from(PERSISTED_FIELDS).some(
            (field) => edit[field] !== undefined
          );
          if (touchedPersistedField) next.add(symbotId);
        });
        return next;
      });
    } catch {
      // ignore corrupt/unavailable sessionStorage payload
    }
  }, [mode, customerId]);

  // keep sessionStorage in sync with every staged edit automatically — this is
  // what the removed Save button used to do on click; now it just always happens
  useEffect(() => {
    if (mode !== 'manual') return;
    persistManualStagedEdits(stagedEdits);
  }, [mode, stagedEdits]);

  const assignmentsByTruckBumper = useMemo(() => {
    const map: Record<string, ManualDispatchAssignment> = {};
    activeAssignments.forEach((a) => {
      map[a.truckBumper] = a;
    });
    return map;
  }, [activeAssignments]);

  // symbotId -> override VALUE(S) already confirmed fulfilled via SSE — lets
  // getEffective ignore a stale, not-yet-cleared server override instead of
  // waiting up to 30s for the next refreshActivePlan() poll to catch up.
  // Compared against the CURRENT assignment.overrideNode/nextOverrideNode on
  // every read, so it self-corrects once the server value actually changes —
  // no cleanup needed.
  const [fulfilledOverrideValues, setFulfilledOverrideValues] = useState<
    Record<string, { current?: string; next?: string }>
  >({});

  // the SSE effect below only depends on [accessToken, customerId, customerCode]
  // so it never re-subscribes — its onmessage closure would otherwise see
  // whatever assets/assignments were current at mount forever. Refs let it
  // always read the latest without tearing down the EventSource connection.
  const assetsRef = useRef(assets);
  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);
  const assignmentsByTruckBumperRef = useRef(assignmentsByTruckBumper);
  useEffect(() => {
    assignmentsByTruckBumperRef.current = assignmentsByTruckBumper;
  }, [assignmentsByTruckBumper]);
  const refreshActivePlanRef = useRef(refreshActivePlan);
  useEffect(() => {
    refreshActivePlanRef.current = refreshActivePlan;
  });
  const stagedEditsRef = useRef(stagedEdits);
  useEffect(() => {
    stagedEditsRef.current = stagedEdits;
  }, [stagedEdits]);

  // live SSE always wins over a pending staged edit on the same field — once
  // the backend/live truth for currentAssignment/nextAssignment changes, the
  // dispatcher's not-yet-committed pick on that field is discarded and the
  // truck goes back to "no pending change" (Override disables again) rather
  // than sitting there showing a stale choice on top of fresher live data
  const clearStagedFieldOnSse = (
    symbotId: string,
    field: 'currentAssignment' | 'nextAssignment'
  ) => {
    // manual mode only — auto mode's assignment-override flow is a separate,
    // narrower mechanism (pendingOverrideTrucks) untouched by this change
    if (mode !== 'manual') return;
    const edit = stagedEditsRef.current[symbotId];
    if (!edit || edit[field] === undefined) return;
    const { [field]: _cleared, ...rest } = edit;
    setStagedEdits((prev) => ({ ...prev, [symbotId]: rest }));
    const stillDirty = Array.from(PERSISTED_FIELDS).some(
      (f) => f !== field && edit[f] !== undefined
    );
    if (!stillDirty) {
      setDirtyTrucks((prev) => {
        if (!prev.has(symbotId)) return prev;
        const next = new Set(prev);
        next.delete(symbotId);
        return next;
      });
    }
  };

  const setField = (
    symbotId: string,
    field: keyof TruckEdit,
    value: string
  ) => {
    setStagedEdits((prev) => {
      const next: Partial<TruckEdit> = { ...prev[symbotId], [field]: value };
      // editing the route (current/next) is a manual override of whatever
      // shovel/region assignment was already in place
      if (field === 'currentAssignment' || field === 'nextAssignment') {
        next.assignmentType = 'manual';
      } else if (field === 'shovelLock') {
        // picking a shovel alone means "follow that shovel's own routing" —
        // auto-fill Region Lock from the excavator's own configured dump
        // (set up on the excavator screen), discarding any previous
        // region-lock override so it starts fresh as a Shovel Assignment
        next.assignmentType = 'shovel';
        const config = excavatorConfigs.find(
          (c) => c.excavatorBumper === value
        );
        next.regionLock = config?.dumpingNodes[0] ?? '';
      } else if (field === 'regionLock') {
        // the dispatcher picking a region lock themselves (instead of letting
        // it auto-fill) is a truck-specific lock, distinct from just
        // following the shovel's default
        next.assignmentType = 'locked';
      }
      return { ...prev, [symbotId]: next };
    });
    if (PERSISTED_FIELDS.has(field)) {
      setDirtyTrucks((prev) => new Set(prev).add(symbotId));
    }
  };

  // effective value shown in a cell: a staged dispatcher edit wins first. For
  // Current/Next Assignment in manual mode, the server's own committed divert
  // (overrideNode/nextOverrideNode) is next — without this tier, the cell had
  // nothing to fall back to once the staged edit cleared after a successful
  // commit except raw SSE telemetry (the truck's actual physical routing,
  // unrelated to the divert), which made a just-committed override look like
  // it had silently reverted. Live telemetry stays the last resort.
  const getEffective = (
    symbotId: string,
    field: keyof TruckEdit
  ): string | undefined => {
    const staged = stagedEdits[symbotId]?.[field];
    if (staged !== undefined) return staged;
    if (
      mode === 'manual' &&
      (field === 'currentAssignment' || field === 'nextAssignment')
    ) {
      const truck = assets.find((a) => a.symbotId === symbotId);
      const assignment = truck
        ? assignmentsByTruckBumper[truck.bumperNumber]
        : undefined;
      const serverOverride =
        field === 'currentAssignment'
          ? assignment?.overrideNode
          : assignment?.nextOverrideNode;
      // SSE already confirmed this exact override value was fulfilled — don't
      // show it again just because the server hasn't cleared it yet
      const fulfilled =
        field === 'currentAssignment'
          ? fulfilledOverrideValues[symbotId]?.current
          : fulfilledOverrideValues[symbotId]?.next;
      if (serverOverride != null && serverOverride !== fulfilled) {
        return serverOverride;
      }
    }
    return liveTelemetry[symbotId]?.[field as keyof LiveTelemetry];
  };

  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // ── Theme tokens ─────────────────────────────────────────────────────────

  const bg = enableDarkTheme
    ? theme.palette.background.default
    : theme.palette.grey[100];

  const panelBg = enableDarkTheme
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.background.paper;

  const stickyBg = theme.palette.background.paper;

  const borderColor = enableDarkTheme
    ? alpha(theme.palette.common.white, 0.08)
    : theme.palette.divider;

  const textPrimary = theme.palette.text.primary;
  const textMuted = theme.palette.text.secondary;

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const saveStatus = async () => {
    if (!statusModal) return;
    const { truck, status, reason, description } = statusModal;
    const assetId = getAssetId(truck.symbotId);
    if (!assetId) return;
    setStatusSaving(true);
    try {
      await dispatchApi.updateAssetStatus(
        assetId,
        status,
        reason?.trim() || undefined,
        description?.trim() || undefined
      );
      // track user-initiated down so bell never shows for manual changes
      if (status.toLowerCase() === 'down') {
        setUserSetDown((prev) => {
          const next = new Set(prev);
          next.add(truck.symbotId);
          return next;
        });
      } else {
        setUserSetDown((prev) => {
          const next = new Set(prev);
          next.delete(truck.symbotId);
          return next;
        });
      }
      setLastStatuses((prev) => ({
        ...prev,
        [truck.symbotId]: {
          payload: {
            status,
            reason: reason?.trim() ?? '',
            description: description?.trim() ?? '',
          },
          operatorName: prev[truck.symbotId]?.operatorName ?? null,
          operatorLoggedOut: prev[truck.symbotId]?.operatorLoggedOut,
        },
      }));
      setStatusModal(null);
    } catch (_) {
      // keep modal open so user can retry
    } finally {
      setStatusSaving(false);
    }
  };

  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchAllData = useCallback(
    async (shouldAbort: () => boolean = () => false) => {
      const [
        assetRes,
        statusRes,
        locRes,
        gradeRes,
        materialGradeRes,
        scRes,
        latestRes,
        tripsRes,
      ] = await Promise.allSettled([
        dispatchApi.getAssetInfo(),
        dispatchApi.getAssetLastStatus(),
        locationApi.getLocations(),
        gradeApi.getGrades(),
        materialGradeApi.getAll(),
        statusCodesApi.getAll(),
        dispatchApi.getLatestDispatchAssetStatus(false),
        dispatchApi.getTrips(),
      ]);

      if (shouldAbort()) return;

      const assetJson =
        assetRes.status === 'fulfilled' ? assetRes.value : { assets: [] };
      const statusJson =
        statusRes.status === 'fulfilled' ? statusRes.value : { assets: [] };
      const locationsJson = locRes.status === 'fulfilled' ? locRes.value : [];
      const gradeJson = gradeRes.status === 'fulfilled' ? gradeRes.value : [];
      const materialGradeJson =
        materialGradeRes.status === 'fulfilled' ? materialGradeRes.value : [];
      const scJson = scRes.status === 'fulfilled' ? scRes.value : [];
      const latestJson =
        latestRes.status === 'fulfilled' ? latestRes.value : null;
      const tripsJson = tripsRes.status === 'fulfilled' ? tripsRes.value : null;

      const statusMap: Record<string, LastStatus> = {};
      const initialTelemetry: Record<string, LiveTelemetry> = {};
      const initialTonnage: Record<string, number> = {};
      const initialLoadingPoints: Record<string, string> = {};

      (statusJson.assets ?? []).forEach((a: any) => {
        statusMap[a.symbotId] = {
          payload: a.status?.payload ?? null,
          operatorName: a.status?.operatorName ?? null,
        };
      });

      // seed assignments and tonnage from latest dispatch status (same source as DispatchMonitor)
      (latestJson?.devices ?? []).forEach((d) => {
        const p = d.latest?.payload;
        if (!p) return;
        // mirror the live SSE handler's action gating below — routeFrom/
        // routeTo/nextTo are the same payload shape on TRIP_START,
        // TRIP_COMPLETED, PLAN_KPI_RECALCULATED, AND TRIP_PENDING_UPDATED
        // (a manual next-assignment override notice), but only TRIP_START
        // means the truck's current/next leg actually changed — trusting
        // any action here showed the *next* trip's destination as the
        // *current* one the moment a manual override was staged.
        const action = d.latest?.action;
        // nextFrom is always wherever the truck just arrived (== routeTo) —
        // it's the START of the next leg, not its destination. nextTo is
        // the actual "what's coming next" value.
        if (
          action === 'TRIP_START' &&
          (p.routeTo != null || p.nextTo != null)
        ) {
          initialTelemetry[d.deviceId] = {
            ...(p.routeTo != null && { currentAssignment: p.routeTo }),
            ...(p.nextTo != null && { nextAssignment: p.nextTo }),
          };
        }
        if (typeof p.materialDumped === 'number') {
          initialTonnage[d.deviceId] = p.materialDumped;
        }
        if (
          p.routeFrom != null &&
          (action === 'TRIP_COMPLETED' || action === 'PLAN_KPI_RECALCULATED')
        ) {
          initialLoadingPoints[d.deviceId] = p.routeFrom;
        }
      });

      // dispatch-monitor/get-trips returns the latest in-flight trip per
      // device — same TRIP_START payload shape as above, just keyed
      // directly by device instead of wrapped in devices[]. It's the
      // freshest "what's this truck doing right now" source, so it wins
      // over the get-latest-dispatch-asset-status seed above when both
      // have data for the same device.
      Object.entries(tripsJson?.trips ?? {}).forEach(([deviceId, trip]) => {
        const p = trip?.payload;
        if (!p) return;
        if (
          trip.action === 'TRIP_START' &&
          (p.routeTo != null || p.nextTo != null)
        ) {
          initialTelemetry[deviceId] = {
            ...(p.routeTo != null && { currentAssignment: p.routeTo }),
            ...(p.nextTo != null && { nextAssignment: p.nextTo }),
          };
        }
        if (typeof p.materialDumped === 'number') {
          initialTonnage[deviceId] = p.materialDumped;
        }
        if (
          p.routeFrom != null &&
          (trip.action === 'TRIP_COMPLETED' ||
            trip.action === 'PLAN_KPI_RECALCULATED')
        ) {
          initialLoadingPoints[deviceId] = p.routeFrom;
        }
      });

      initialDownAtLoad.current = new Set(
        Object.entries(statusMap)
          .filter(([, s]) => s.payload?.status?.toLowerCase() === 'down')
          .map(([symbotId]) => symbotId)
      );

      setAssets(assetJson.assets ?? []);
      setLastStatuses(statusMap);
      setLiveTelemetry(initialTelemetry);
      setSseTonnage(initialTonnage);
      setLoadingPointMap(initialLoadingPoints);
      setAllLoadingLocations(locationsJson);
      setGrades(gradeJson);
      setMaterialGrades(materialGradeJson);
      setStatusCodes(scJson);
    },
    [dispatchApi, locationApi, gradeApi, materialGradeApi, statusCodesApi]
  );

  useEffect(() => {
    let cancelled = false;
    fetchAllData(() => cancelled).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchAllData]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchAllData();
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchAllData]);

  // ── SSE — live status updates ─────────────────────────────────────────────

  useEffect(() => {
    if (!accessToken || !customerId) return;
    const base = process.env.REACT_APP_MANAGEMENT_API_URL ?? '';
    const es = new EventSourcePolyfill(
      `${base}/api/dispatch-monitor/live-dispatch-sse?customerId=${customerId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const flushSseLogs = () => {
      sseLogFlushTimerRef.current = null;
      if (pendingSseLogsRef.current.length === 0) return;
      const batch = pendingSseLogsRef.current;
      pendingSseLogsRef.current = [];
      setSseLogs((prev) => {
        const next = [...batch, ...prev];
        return next.length > MAX_SSE_LOGS ? next.slice(0, MAX_SSE_LOGS) : next;
      });
    };

    es.onmessage = (e: MessageEvent) => {
      const pushSseLog = (entry: Omit<SseLogEntry, 'id' | 'timestamp'>) => {
        if (!canViewSseLogs) return;
        sseLogSeqRef.current += 1;
        // newest-first, matching render order — buffered here and flushed on
        // a timer rather than via setSseLogs on every single message
        pendingSseLogsRef.current.unshift({
          ...entry,
          id: `${Date.now()}-${sseLogSeqRef.current}`,
          timestamp: Date.now(),
        });
        if (pendingSseLogsRef.current.length > MAX_SSE_LOGS) {
          pendingSseLogsRef.current.length = MAX_SSE_LOGS;
        }
        if (sseLogFlushTimerRef.current == null) {
          sseLogFlushTimerRef.current = setTimeout(flushSseLogs, 300);
        }
      };

      let outer: { channel: string; message: string | Record<string, any> };
      try {
        outer = JSON.parse(e.data as string);
      } catch (_) {
        pushSseLog({
          channel: '',
          action: null,
          device: null,
          asset: null,
          details: null,
          batteryLevel: null,
          name: null,
          raw: String(e.data),
          isJson: false,
        });
        return;
      }

      // heartbeat pings carry no useful info — skip logging them entirely
      if ((outer as Record<string, any> | null)?.sse_heartbeat) return;

      // not the {channel, message} envelope — some other non-heartbeat
      // shape. Log the raw payload as-is instead of reaching into a
      // `message` field that doesn't exist on it.
      if (typeof outer?.channel !== 'string' || outer?.message === undefined) {
        pushSseLog({
          channel: (outer as Record<string, any> | null)?.channel ?? '',
          action: null,
          device: null,
          asset: null,
          details: null,
          batteryLevel: null,
          name: null,
          raw: JSON.stringify(outer, null, 2),
          isJson: true,
        });
        return;
      }

      let msg: Record<string, any>;
      try {
        msg = (
          typeof outer.message === 'string'
            ? JSON.parse(outer.message)
            : outer.message
        ) as Record<string, any>;
      } catch (_) {
        pushSseLog({
          channel: outer.channel,
          action: null,
          device: null,
          asset: null,
          details: null,
          batteryLevel: null,
          name: null,
          raw:
            typeof outer.message === 'string'
              ? outer.message
              : JSON.stringify(outer.message),
          isJson: false,
        });
        return;
      }

      try {
        pushSseLog({
          channel: outer.channel,
          action: (msg?.action as string | undefined) ?? null,
          device: (msg?.device as string | undefined) ?? null,
          asset: (msg?.asset as string | undefined) ?? null,
          details: (msg?.details as string | undefined) ?? null,
          batteryLevel: (msg?.batteryLevel as number | undefined) ?? null,
          name: (msg?.name as string | undefined) ?? null,
          raw: JSON.stringify(msg, null, 2),
          isJson: true,
        });

        const device = msg?.device as string | undefined;
        if (!device) return;

        // status pings (SHIFT_END / down / etc.) arrive on this channel
        // alongside plan/trip actions — handle status here, then fall
        // through so plan/trip actions on the same channel still reach the
        // routeFrom/routeTo/nextTo handling below instead of returning early.
        if (outer.channel === `xtab:${customerCode}:last:pubsub`) {
          if (msg?.action === 'SHIFT_END') return;
          if (msg?.action === 'LOGIN') {
            const loginOperatorName = msg?.operatorName as string | undefined;
            if (loginOperatorName) {
              setLastStatuses((prev) => ({
                ...prev,
                [device]: {
                  payload: prev[device]?.payload ?? null,
                  operatorName: loginOperatorName,
                  operatorLoggedOut: false,
                },
              }));
            }
            return;
          }
          if (msg?.action === 'LOGOUT') {
            const logoutOperatorName = msg?.operatorName as string | undefined;
            if (logoutOperatorName) {
              setLastStatuses((prev) => ({
                ...prev,
                [device]: {
                  payload: prev[device]?.payload ?? null,
                  operatorName: logoutOperatorName,
                  operatorLoggedOut: true,
                },
              }));
            }
            return;
          }
          const payloadStatus = msg?.payload?.status as string | undefined;
          const reason = msg?.payload?.reason as string | undefined;
          const description = msg?.payload?.description as string | undefined;
          if (payloadStatus) {
            setLastStatuses((prev) => ({
              ...prev,
              [device]: {
                payload: {
                  status: payloadStatus,
                  reason: reason ?? '',
                  description: description ?? '',
                },
                operatorName: prev[device]?.operatorName ?? null,
                operatorLoggedOut: prev[device]?.operatorLoggedOut,
              },
            }));
            // when status recovers from down, reset confirmation so next SSE "down" shows the bell again
            if (payloadStatus.toLowerCase() !== 'down') {
              setConfirmedDown((prev) => {
                if (!prev.has(device)) return prev;
                const next = new Set(prev);
                next.delete(device);
                return next;
              });
              setUserSetDown((prev) => {
                if (!prev.has(device)) return prev;
                const next = new Set(prev);
                next.delete(device);
                return next;
              });
            }
          }
        }

        // KPI / plan recalculation and trip-start/completion payloads
        // (routeTo, nextTo, materialDumped) — same payload shape for all three.
        // TRIP_PAYLOAD_UPDATED/TRIP_PENDING_UPDATED/TRIP_ROUTE_CHANGED are
        // included too — they're how the backend reports a route change
        // without a full TRIP_START: TRIP_ROUTE_CHANGED fires the instant a
        // CURRENT-trip override commits (routeFrom/routeTo describe the
        // current leg's new destination); TRIP_PENDING_UPDATED (routeTo) and
        // TRIP_PAYLOAD_UPDATED (nextTo) report the NEXT leg once the backend
        // works it out mid-trip. Dropping any of these left Current/Next
        // Assignment stuck on stale data even after the backend knew the
        // real answer.
        if (
          msg?.action !== 'PLAN_KPI_RECALCULATED' &&
          msg?.action !== 'TRIP_COMPLETED' &&
          msg?.action !== 'TRIP_START' &&
          msg?.action !== 'TRIP_PAYLOAD_UPDATED' &&
          msg?.action !== 'TRIP_PENDING_UPDATED' &&
          msg?.action !== 'TRIP_ROUTE_CHANGED'
        ) {
          return;
        }
        const routeFrom = msg?.payload?.routeFrom as string | undefined;
        const routeTo = msg?.payload?.routeTo as string | undefined;
        // nextFrom is always wherever the truck just arrived (== routeTo) —
        // it's the START of the next leg, not its destination. nextTo is the
        // actual "what's coming next" value.
        const nextTo = msg?.payload?.nextTo as string | undefined;
        const materialDumped = msg?.payload?.materialDumped as
          | number
          | undefined;
        // routeFrom only reflects the truck's actual current/just-arrived
        // location on these three — TRIP_START's routeFrom can be a dump
        // (the leg just finished), and TRIP_PENDING_UPDATED's routeFrom is
        // the START of the NEXT (not current) leg, so neither belongs here.
        if (
          routeFrom != null &&
          (msg.action === 'TRIP_COMPLETED' ||
            msg.action === 'PLAN_KPI_RECALCULATED' ||
            msg.action === 'TRIP_ROUTE_CHANGED')
        ) {
          setLoadingPointMap((prev) => ({ ...prev, [device]: routeFrom }));
        }
        // TRIP_ROUTE_CHANGED fires the instant a CURRENT-trip override
        // commits — its routeTo is the new current-leg destination, arriving
        // ahead of (or even instead of) any TRIP_START re-fire.
        if (msg.action === 'TRIP_ROUTE_CHANGED' && routeTo != null) {
          setLiveTelemetry((prev) => ({
            ...prev,
            [device]: { ...prev[device], currentAssignment: routeTo },
          }));
          clearStagedFieldOnSse(device, 'currentAssignment');
        }
        // TRIP_PAYLOAD_UPDATED/TRIP_PENDING_UPDATED never carry a
        // current-leg change, only a freshly-resolved next leg (nextTo on
        // the former, routeTo on the latter) — apply it to nextAssignment
        // alone, filling in what TRIP_START left blank instead of waiting
        // for the next TRIP_START to catch up.
        if (msg.action === 'TRIP_PAYLOAD_UPDATED' && nextTo != null) {
          setLiveTelemetry((prev) => ({
            ...prev,
            [device]: { ...prev[device], nextAssignment: nextTo },
          }));
          clearStagedFieldOnSse(device, 'nextAssignment');
        }
        if (msg.action === 'TRIP_PENDING_UPDATED' && routeTo != null) {
          setLiveTelemetry((prev) => ({
            ...prev,
            [device]: { ...prev[device], nextAssignment: routeTo },
          }));
          clearStagedFieldOnSse(device, 'nextAssignment');
        }
        // current/next assignment otherwise only come from TRIP_START —
        // that's the moment the truck's route actually changes; TRIP_COMPLETED /
        // PLAN_KPI_RECALCULATED still update tonnage/loadingPoint below but
        // shouldn't touch the assignment columns.
        if (
          msg.action === 'TRIP_START' &&
          (routeTo != null || nextTo != null)
        ) {
          setLiveTelemetry((prev) => {
            // nextTo is null when the backend doesn't know the leg after
            // this one yet — must actively clear a stale nextAssignment
            // here, not just skip updating it. Left stale, a later
            // TRIP_START whose routeTo happens to equal that stale value
            // (the truck arriving exactly where "next" already said it
            // would) gets excluded from its own Current Assignment options
            // pool by the current/next mutual "exclude self" filter
            // (getCurrentAssignmentOptions), rendering the cell blank even
            // though getEffective resolves the correct value.
            const { nextAssignment: _staleNext, ...rest } = prev[device] ?? {};
            return {
              ...prev,
              [device]: {
                ...rest,
                ...(routeTo != null && { currentAssignment: routeTo }),
                ...(nextTo != null && { nextAssignment: nextTo }),
              },
            };
          });
          if (routeTo != null)
            clearStagedFieldOnSse(device, 'currentAssignment');
          // nextAssignment in liveTelemetry gets touched/cleared above
          // regardless of nextTo's nullness, so a staged pick on it is
          // just as stale either way — clear it unconditionally too
          clearStagedFieldOnSse(device, 'nextAssignment');
          // TRIP_START always wins over a sticky manual/shovel override —
          // mark any active override fulfilled immediately (using its own
          // value) instead of waiting for a later routeTo to happen to match
          // it, so getEffective falls through to the fresh liveTelemetry.
          const startedTruck = assetsRef.current.find(
            (a) => a.symbotId === device
          );
          const startedAssignment = startedTruck
            ? assignmentsByTruckBumperRef.current[startedTruck.bumperNumber]
            : undefined;
          if (
            startedAssignment?.overrideNode != null ||
            startedAssignment?.nextOverrideNode != null
          ) {
            setFulfilledOverrideValues((prev) => ({
              ...prev,
              [device]: {
                ...prev[device],
                ...(startedAssignment.overrideNode != null && {
                  current: startedAssignment.overrideNode,
                }),
                ...(startedAssignment.nextOverrideNode != null && {
                  next: startedAssignment.nextOverrideNode,
                }),
              },
            }));
            void refreshActivePlanRef.current();
          }
        }
        if (materialDumped != null) {
          setSseTonnage((prev) => ({ ...prev, [device]: materialDumped }));
        }
        // a trip just completed to routeTo — if that's the target of an
        // active current/next override, mark it fulfilled so getEffective
        // stops showing it, and refresh now instead of waiting up to 30s for
        // the server's own overrideNode/nextOverrideNode to clear
        if (routeTo != null) {
          const truck = assetsRef.current.find((a) => a.symbotId === device);
          const assignment = truck
            ? assignmentsByTruckBumperRef.current[truck.bumperNumber]
            : undefined;
          if (assignment) {
            const updates: { current?: string; next?: string } = {};
            if (
              assignment.overrideNode != null &&
              assignment.overrideNode === routeTo
            ) {
              updates.current = routeTo;
            }
            if (
              assignment.nextOverrideNode != null &&
              assignment.nextOverrideNode === routeTo
            ) {
              updates.next = routeTo;
            }
            if (updates.current !== undefined || updates.next !== undefined) {
              setFulfilledOverrideValues((prev) => ({
                ...prev,
                [device]: { ...prev[device], ...updates },
              }));
              void refreshActivePlanRef.current();
            }
          }
        }
      } catch (_) {}
    };
    es.onerror = () => {};
    return () => {
      es.close();
      if (sseLogFlushTimerRef.current != null) {
        clearTimeout(sseLogFlushTimerRef.current);
        sseLogFlushTimerRef.current = null;
      }
    };
  }, [accessToken, customerId, customerCode]);

  // ── Scroll hint ───────────────────────────────────────────────────────────

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const check = () => {
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    check();
    el.addEventListener('scroll', check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [loading]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const statusGroups = useMemo(() => {
    const map = new Map<string, { colorCode: string; reasons: string[] }>();
    statusCodes.forEach((sc) => {
      const key = sc.status.trim().toLowerCase();
      if (!map.has(key)) {
        map.set(key, { colorCode: sc.colorCode, reasons: [] });
      }
      const group = map.get(key);
      const cat = sc.reasonCategory?.trim();
      if (group && cat && !group.reasons.includes(cat)) {
        group.reasons.push(cat);
      }
    });
    return map;
  }, [statusCodes]);

  // status+reasonCategory → every configured description for that pair (a
  // reason category can have several status codes, each with its own
  // description, e.g. "Fuel" → "Fuel alpha" / "Traveling To Fuelshop" /
  // "Fuel Ran Out") — feeds the Description select in the status modal.
  const descriptionsByStatusReason = useMemo(() => {
    const map = new Map<string, string[]>();
    statusCodes.forEach((sc) => {
      const statusKey = sc.status?.trim().toLowerCase();
      const reasonKey = sc.reasonCategory?.trim().toLowerCase();
      if (!statusKey || !reasonKey || !sc.description) return;
      const key = `${statusKey}__${reasonKey}`;
      const list = map.get(key);
      if (list) {
        if (!list.includes(sc.description)) list.push(sc.description);
      } else {
        map.set(key, [sc.description]);
      }
    });
    return map;
  }, [statusCodes]);

  const getDescriptionOptions = useCallback(
    (status: string | null | undefined, reason: string | null | undefined) =>
      descriptionsByStatusReason.get(
        `${status?.trim().toLowerCase() ?? ''}__${
          reason?.trim().toLowerCase() ?? ''
        }`
      ) ?? [],
    [descriptionsByStatusReason]
  );

  // Description is the specific, dispatcher-picked note for the truck's
  // current reason category — prefer it, but fall back to the raw reason
  // category for statuses set before this field existed.
  const resolveReasonDisplay = useCallback(
    (
      _status: string | null | undefined,
      reason: string | null | undefined,
      description?: string | null
    ): string | null => {
      const trimmedDescription = description?.trim();
      if (trimmedDescription) return trimmedDescription;
      return reason ?? null;
    },
    []
  );

  // loading location name → material (case-insensitive, first match wins).
  // Grades are configured per loading point (g.locationName), not by grade
  // name — matching on g.name here was the root cause of Material showing empty.
  const locationMaterialMap = useMemo(() => {
    const map = new Map<string, string>();
    grades.forEach((g) => {
      const key = g.locationName?.trim().toLowerCase();
      if (key && g.materialCategory && !map.has(key)) {
        map.set(key, g.materialCategory);
      }
    });
    return map;
  }, [grades]);

  // locationName → locationType (case-insensitive), used to tell whether a
  // given assignment slot (current/next) is the loading point or a dump point
  const locationTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    allLocations.forEach((l) => {
      const key = l.name?.trim().toLowerCase();
      if (key) map.set(key, l.locationType?.trim().toLowerCase() ?? '');
    });
    return map;
  }, [allLocations]);

  // Current/Next Assignment no longer carry the loading point (they're
  // routeTo/nextTo) — loadingPointMap is fed directly from routeFrom via
  // telemetry/SSE and is the loading point, so no location-type guessing is
  // needed. This is only the auto-mode/fallback path — manual mode reads
  // material straight off the active plan's assignment (already resolved
  // server-side).
  const resolveMaterialFromLocations = (truck: AssetInfo): string => {
    const loadingName = loadingPointMap[truck.symbotId];
    if (!loadingName) return '—';
    return locationMaterialMap.get(loadingName.trim().toLowerCase()) ?? '—';
  };

  const resolveTruckMaterial = (truck: AssetInfo): string => {
    if (mode === 'manual') {
      const assignment = assignmentsByTruckBumper[truck.bumperNumber];
      if (assignment?.material) return assignment.material;
    }
    return resolveMaterialFromLocations(truck);
  };

  // (make, model, materialCategory) → tonnage, case-insensitive
  const materialGradeTonnageMap = useMemo(() => {
    const map = new Map<string, number>();
    materialGrades.forEach((mg) => {
      const key = [mg.make, mg.model, mg.materialCategory]
        .map((v) => v?.trim().toLowerCase())
        .join('|');
      if (!map.has(key)) map.set(key, mg.tonnage);
    });
    return map;
  }, [materialGrades]);

  const getMaterialCategoryTonnage = (
    truck: AssetInfo,
    material: string
  ): number | null => {
    if (!truck.makeModelDetails || material === '—') return null;
    const key = [
      truck.makeModelDetails.make,
      truck.makeModelDetails.model,
      material,
    ]
      .map((v) => v?.trim().toLowerCase())
      .join('|');
    return materialGradeTonnageMap.get(key) ?? null;
  };

  // assets with no linked device can't report status or position — exclude
  // them everywhere rather than showing an uncontrollable "No Status" row
  const haulTrucks = useMemo(
    () =>
      assets.filter(
        (a) => a.assetType.trim().toLowerCase() === 'haul_truck' && a.symbotId
      ),
    [assets]
  );

  // shovel lock / loading equipment can be either an excavator or a loader
  const excavators = useMemo(
    () =>
      assets.filter(
        (a) =>
          ['excavator', 'loader'].includes(a.assetType.trim().toLowerCase()) &&
          a.symbotId
      ),
    [assets]
  );

  // truckBumperNumber -> excavator bumper that has it locked
  const shovelLockMap = useMemo(() => {
    const map: Record<string, string> = {};
    excavators.forEach((exc) => {
      const locked = exc.meta?.lockedTrucks;
      if (!Array.isArray(locked)) return;
      (locked as string[]).forEach((truckBumper) => {
        map[truckBumper] = exc.bumperNumber;
      });
    });
    return map;
  }, [excavators]);

  // Region Lock should only ever be a dump/crusher/stockpile destination,
  // never a loading, parking, fueling, or intersection point
  const regionLockLocations = useMemo(
    () =>
      activeLocations.filter((l) => {
        const lt = l.locationType?.trim()?.toLowerCase() ?? '';
        return DUMP_LOCATION_TYPES.has(lt) || CRUSHER_LOCATION_TYPES.has(lt);
      }),
    [activeLocations]
  );

  // bumper numbers of excavators currently hard-locked on the excavator screen —
  // trucks locked to one of these must not have Shovel/Region Lock editable here
  const hardLockedExcavatorBumpers = useMemo(() => {
    const set = new Set<string>();
    excavators.forEach((exc) => {
      if (exc.meta?.hardlock) set.add(exc.bumperNumber);
    });
    return set;
  }, [excavators]);

  const loadingLocationExcavatorMap = useMemo(
    () => buildLoadingLocationExcavatorMap(excavators),
    [excavators]
  );

  const getAssignmentDisplayName = (locationName: string): string =>
    getAssignmentDisplayNameShared(locationName, loadingLocationExcavatorMap);

  // resolve which excavator is stationed at a loading-point location name
  const excavatorBumperForLoadingLocation = (
    locationName: string
  ): string | undefined => loadingLocationExcavatorMap[locationName];

  // resolve a location name (from a staged Current/Next Assignment edit) into
  // the discriminated nodeType + type-specific id shape
  // PATCH /manual-dispatch/plan/trucks expects (Backend Update #10) — no
  // excavatorBumper needed for a loading divert, gradeId alone resolves it
  // server-side
  const buildTruckDivert = (
    locationName: string
  ): ManualDispatchTruckDivert | null => {
    const loc = activeLocations.find((l) => l.name === locationName);
    if (!loc) return null;
    const nodeType = resolveNodeType(loc.locationType);
    if (nodeType === 'loading') {
      const excavatorBumper = excavatorBumperForLoadingLocation(loc.name);
      if (!excavatorBumper) return null;
      const derived = resolveExcavatorGradeAndDump(excavatorBumper);
      if (!derived) return null;
      return { nodeType: 'loading', gradeId: derived.gradeId };
    }
    if (nodeType === 'parking') {
      return { nodeType: 'parking', parkingLocationId: loc.locationId };
    }
    return { nodeType: 'dumping', dumpingZoneId: loc.locationId };
  };

  // derive an excavator's own gradeId/dumpingZoneId from its plan config
  // (set up on the excavator screen) — available even when it currently has
  // zero locked trucks, unlike deriving from a sibling truck's assignment
  const resolveExcavatorGradeAndDump = (
    excavatorBumper: string
  ): { gradeId: string; dumpingZoneId: string } | null => {
    const config = excavatorConfigs.find(
      (c) => c.excavatorBumper === excavatorBumper
    );
    const dumpingZoneId = config?.dumpingZoneIds[0];
    if (!config || !dumpingZoneId) return null;
    return { gradeId: config.gradeId, dumpingZoneId };
  };

  // manual mode: Save just persists staged edits to sessionStorage — no
  // backend call at all. Nothing reaches the backend until Override Plan
  // commits it via commitManualPlan (Backend Update #10)
  const persistManualStagedEdits = (
    edits: Record<string, Partial<TruckEdit>>
  ) => {
    if (!customerId) return;
    try {
      if (Object.keys(edits).length === 0) {
        sessionStorage.removeItem(MANUAL_STAGED_EDITS_KEY_PREFIX + customerId);
      } else {
        sessionStorage.setItem(
          MANUAL_STAGED_EDITS_KEY_PREFIX + customerId,
          JSON.stringify(edits)
        );
      }
    } catch {
      // sessionStorage unavailable — edits still live in memory for this session
    }
  };

  // manual mode: Override Plan is the ONLY action that touches the backend —
  // one atomic commit of every dirty truck's full desired state (shovel/region
  // lock, assignment type intent, current/next diverts), replacing the old
  // pattern of picking between addAssignment/override(loading)/override(dumping)
  // per field. See docs/manual-dispatch-backend-update-10.md.
  const commitManualPlan = async () => {
    if (dirtyTrucks.size === 0) return;
    setIsOverriding(true);
    try {
      const assignments: ManualDispatchTruckCommitInput[] = [];
      const committedSymbotIds: string[] = [];
      // per-truck, which staged fields failed to resolve (and to what target)
      // — surfaced to the dispatcher and rolled back so a truck never keeps
      // showing a picked destination that was silently never sent (Backend
      // Update #10 diverts require the target excavator to have its own
      // grade/dump config set up on the Excavator screen first)
      const unresolvedDiverts: Array<{
        symbotId: string;
        truckBumper: string;
        field: 'currentAssignment' | 'nextAssignment';
        target: string;
      }> = [];

      for (const symbotId of Array.from(dirtyTrucks)) {
        const truck = assets.find((a) => a.symbotId === symbotId);
        const edit = stagedEdits[symbotId];
        if (!truck || !edit) continue;

        const input: ManualDispatchTruckCommitInput = {
          truckBumper: truck.bumperNumber,
        };
        let hasField = false;

        if (edit.shovelLock) {
          input.excavatorBumper = edit.shovelLock;
          hasField = true;
        }
        if (edit.regionLock !== undefined) {
          const dumpLoc = activeLocations.find(
            (l) => l.name === edit.regionLock
          );
          if (dumpLoc) {
            input.dumpingZoneId = dumpLoc.locationId;
            hasField = true;
          }
        }
        if (
          edit.assignmentType === 'shovel' ||
          edit.assignmentType === 'locked'
        ) {
          input.assignmentType = edit.assignmentType;
        }
        if (edit.currentAssignment !== undefined) {
          const divert = buildTruckDivert(edit.currentAssignment);
          if (divert) {
            input.currentAssignment = divert;
            hasField = true;
          } else {
            unresolvedDiverts.push({
              symbotId,
              truckBumper: truck.bumperNumber,
              field: 'currentAssignment',
              target: getAssignmentDisplayName(edit.currentAssignment),
            });
          }
        }
        if (edit.nextAssignment !== undefined) {
          const divert = buildTruckDivert(edit.nextAssignment);
          if (divert) {
            input.nextAssignment = divert;
            hasField = true;
          } else {
            unresolvedDiverts.push({
              symbotId,
              truckBumper: truck.bumperNumber,
              field: 'nextAssignment',
              target: getAssignmentDisplayName(edit.nextAssignment),
            });
          }
        }

        // a brand-new shovel lock with no explicit region lock, and nothing to
        // denormalize from the target excavator's own config (it has no dump
        // zone set up at all) — need the dispatcher to pick one manually
        const excavatorBumper = input.excavatorBumper;
        if (
          excavatorBumper &&
          !input.dumpingZoneId &&
          !resolveExcavatorGradeAndDump(excavatorBumper)
        ) {
          setQueuedAssignmentPickers((prev) => [
            ...prev,
            { symbotId, excavatorBumper, dumpingZoneId: '' },
          ]);
          continue;
        }

        if (hasField) {
          assignments.push(input);
          committedSymbotIds.push(symbotId);
        }
      }

      if (assignments.length > 0) {
        await manualDispatchApi.patchTrucks({ assignments });
      }

      const committedSet = new Set(committedSymbotIds);
      // an unresolved field never reached the backend — clear it from the
      // staged edit rather than leaving the dropdown showing a destination
      // that was silently never sent (a committed truck's whole entry is
      // already dropped below, so only handle the not-committed case here)
      const failedFieldsBySymbotId = new Map<
        string,
        Set<'currentAssignment' | 'nextAssignment'>
      >();
      unresolvedDiverts.forEach((u) => {
        if (committedSet.has(u.symbotId)) return;
        const set = failedFieldsBySymbotId.get(u.symbotId) ?? new Set();
        set.add(u.field);
        failedFieldsBySymbotId.set(u.symbotId, set);
      });
      if (committedSymbotIds.length > 0 || failedFieldsBySymbotId.size > 0) {
        setStagedEdits((prev) => {
          const next: Record<string, Partial<TruckEdit>> = {};
          Object.entries(prev).forEach(([id, edit]) => {
            if (committedSet.has(id)) return;
            const failedFields = failedFieldsBySymbotId.get(id);
            if (!failedFields) {
              next[id] = edit;
              return;
            }
            let reverted: Partial<TruckEdit> = { ...edit };
            if (failedFields.has('currentAssignment')) {
              const { currentAssignment: _currentAssignment, ...rest } =
                reverted;
              reverted = rest;
            }
            if (failedFields.has('nextAssignment')) {
              const { nextAssignment: _nextAssignment, ...rest } = reverted;
              reverted = rest;
            }
            if (Object.keys(reverted).length > 0) next[id] = reverted;
          });
          persistManualStagedEdits(next);
          return next;
        });
        setDirtyTrucks((prev) => {
          const next = new Set(prev);
          committedSymbotIds.forEach((id) => next.delete(id));
          failedFieldsBySymbotId.forEach((failedFields, symbotId) => {
            const stillDirty = Array.from(PERSISTED_FIELDS).some((field) => {
              const isFailedField =
                field === 'currentAssignment' || field === 'nextAssignment'
                  ? failedFields.has(field)
                  : false;
              return (
                !isFailedField && stagedEdits[symbotId]?.[field] !== undefined
              );
            });
            if (!stillDirty) next.delete(symbotId);
          });
          return next;
        });
      }

      await refreshActivePlan();
      if (unresolvedDiverts.length > 0) {
        dispatch(
          showNotification({
            message: unresolvedDiverts
              .map((u) =>
                t('hm_divert_unresolved_detail', {
                  truckBumper: u.truckBumper,
                  target: u.target,
                })
              )
              .join('; '),
            type: 'longError',
          })
        );
      } else if (assignments.length > 0) {
        dispatch(
          showNotification({
            message: t('hm_override_success'),
            type: 'success',
          })
        );
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.data?.error?.detail ??
        err?.response?.data?.message ??
        t('hm_override_error');
      dispatch(showNotification({ message, type: 'error' }));
    } finally {
      setIsOverriding(false);
    }
  };

  // AUTO MODE ONLY — manual mode's Save is local-only (see saveManualLocally);
  // the Save button routes there instead and this function never runs for it
  const saveAll = async () => {
    if (dirtyTrucks.size === 0) return;
    setIsSavingAll(true);
    try {
      for (const symbotId of Array.from(dirtyTrucks)) {
        const truck = assets.find((a) => a.symbotId === symbotId);
        const edit = stagedEdits[symbotId];
        if (!truck || !edit) continue;

        if (edit.regionLock !== undefined) {
          const assetId = getAssetId(symbotId);
          if (assetId) {
            await assetApi.updateMeta(assetId, {
              ...(truck.meta ?? {}),
              regionLock: edit.regionLock,
            });
          }
        }

        if (edit.shovelLock !== undefined) {
          const newExcBumper = edit.shovelLock;
          const prevExcBumper = shovelLockMap[truck.bumperNumber] ?? '';
          if (prevExcBumper !== newExcBumper) {
            if (prevExcBumper) {
              const oldExc = excavators.find(
                (x) => x.bumperNumber === prevExcBumper
              );
              const oldAssetId = oldExc ? getAssetId(oldExc.symbotId) : null;
              if (oldExc && oldAssetId) {
                const prevLocked = Array.isArray(oldExc.meta?.lockedTrucks)
                  ? (oldExc.meta?.lockedTrucks as string[])
                  : [];
                await assetApi.updateMeta(oldAssetId, {
                  ...(oldExc.meta ?? {}),
                  lockedTrucks: prevLocked.filter(
                    (b) => b !== truck.bumperNumber
                  ),
                });
              }
            }

            if (newExcBumper) {
              const newExc = excavators.find(
                (x) => x.bumperNumber === newExcBumper
              );
              const newAssetId = newExc ? getAssetId(newExc.symbotId) : null;
              if (newExc && newAssetId) {
                const prevLocked = Array.isArray(newExc.meta?.lockedTrucks)
                  ? (newExc.meta?.lockedTrucks as string[])
                  : [];
                if (!prevLocked.includes(truck.bumperNumber)) {
                  await assetApi.updateMeta(newAssetId, {
                    ...(newExc.meta ?? {}),
                    lockedTrucks: [...prevLocked, truck.bumperNumber],
                  });
                }
              }
            }
          }
        }

        setPendingOverrideTrucks((prev) => new Set(prev).add(symbotId));

        // once persisted, defer back to fresh server data (shovelLockMap /
        // assignmentsByTruckBumper) instead of pinning the display to this
        // edit forever — otherwise later changes made elsewhere (e.g. the
        // excavator screen moving this truck to a different excavator) would
        // never show up here. Only clear shovel/region/assignmentType when
        // THIS round actually touched shovel/region — a truck can be dirty
        // here purely from a staged currentAssignment/nextAssignment edit
        // (Save doesn't act on those, Override Plan does), and clearing
        // assignmentType in that case would wipe out the "Manual Assignment"
        // tag those edits just set, even though nothing was actually saved
        if (edit.shovelLock !== undefined || edit.regionLock !== undefined) {
          setStagedEdits((prev) => {
            const savedEdit = prev[symbotId];
            if (!savedEdit) return prev;
            const {
              shovelLock: _shovelLock,
              regionLock: _regionLock,
              assignmentType: _assignmentType,
              ...rest
            } = savedEdit;
            return { ...prev, [symbotId]: rest };
          });
        }
      }

      setDirtyTrucks(new Set());
      dispatch(
        showNotification({ message: t('hm_save_success'), type: 'success' })
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.data?.error?.detail ??
        err?.response?.data?.message ??
        t('hm_save_error');
      dispatch(showNotification({ message, type: 'error' }));
    } finally {
      setIsSavingAll(false);
    }
  };

  // AUTO MODE ONLY — manual mode's commit is commitManualPlan, using the
  // atomic PATCH /manual-dispatch/plan/trucks endpoint (Backend Update #10)
  const overridePlan = async () => {
    if (pendingOverrideTrucks.size === 0) return;
    setIsOverriding(true);
    try {
      const allBumperNumbers = haulTrucks.map((tr) => tr.bumperNumber);

      for (const symbotId of Array.from(pendingOverrideTrucks)) {
        const truck = assets.find((a) => a.symbotId === symbotId);
        if (!truck) continue;
        const currentAssignment = getEffective(symbotId, 'currentAssignment');
        const nextAssignment = getEffective(symbotId, 'nextAssignment');

        if (currentAssignment) {
          const loc = activeLocations.find((l) => l.name === currentAssignment);
          if (loc) {
            const payload: HaulerAssignmentChangePayload = {
              overrideType: 'ASSIGNMENT_CHANGE',
              haulTrucks: allBumperNumbers,
              haulTruckToOverride: truck.bumperNumber,
              assignmentTarget: 'current',
              node: loc.name,
              nodeType: resolveNodeType(loc.locationType),
            };
            await dispatchApi.overrideHaulerMonitor(payload);
          }
        }

        if (nextAssignment) {
          const loc = activeLocations.find((l) => l.name === nextAssignment);
          if (loc) {
            const payload: HaulerAssignmentChangePayload = {
              overrideType: 'ASSIGNMENT_CHANGE',
              haulTrucks: allBumperNumbers,
              haulTruckToOverride: truck.bumperNumber,
              assignmentTarget: 'next',
              scope: 'next_trip',
              node: loc.name,
              nodeType: resolveNodeType(loc.locationType),
            };
            await dispatchApi.overrideHaulerMonitor(payload);
          }
        }

        // once persisted, defer back to fresh live telemetry instead of
        // pinning the display to this edit forever
        setStagedEdits((prev) => {
          const savedEdit = prev[symbotId];
          if (!savedEdit) return prev;
          const {
            currentAssignment: _currentAssignment,
            nextAssignment: _nextAssignment,
            ...rest
          } = savedEdit;
          return { ...prev, [symbotId]: rest };
        });
      }

      setPendingOverrideTrucks(new Set());
      dispatch(
        showNotification({ message: t('hm_override_success'), type: 'success' })
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.data?.error?.detail ??
        err?.response?.data?.message ??
        t('hm_override_error');
      dispatch(showNotification({ message, type: 'error' }));
    } finally {
      setIsOverriding(false);
    }
  };

  // ── Shared cell style ─────────────────────────────────────────────────────

  const tdBase: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: `1px solid ${borderColor}`,
    whiteSpace: 'nowrap',
    fontSize: 12,
    color: textMuted,
  };

  const thBase: React.CSSProperties = {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: textMuted,
    borderBottom: `1px solid ${borderColor}`,
    whiteSpace: 'nowrap',
    backgroundColor: panelBg,
  };

  const NON_STICKY_COLS = [
    { key: 'equipmentType', label: t('hm_col_equipment_type') },
    { key: 'operator', label: t('hm_col_operator') },
    { key: 'status', label: t('hm_col_status_reason') },
    { key: 'activity', label: t('hm_col_activity') },
    { key: 'shovelLock', label: t('hm_col_shovel_lock') },
    { key: 'regionLock', label: t('hm_col_region_lock') },
    { key: 'tonnage', label: t('hm_col_tonnage') },
    { key: 'material', label: t('hm_col_material') },
    { key: 'currentAssignment', label: t('hm_col_current_assignment') },
    { key: 'assignmentType', label: t('hm_col_assignment_type') },
    { key: 'nextAssignment', label: t('hm_col_next_assignment') },
  ];

  // Mirrors the same formulas the row cells below compute inline — kept as
  // a parallel set of getters (rather than refactoring the row render to
  // consume them) so sorting can't accidentally change what's displayed.
  const getRowShovelLock = (truck: AssetInfo): string | null =>
    mode === 'manual'
      ? assignmentsByTruckBumper[truck.bumperNumber]?.excavatorBumper ?? null
      : shovelLockMap[truck.bumperNumber] ?? null;

  const getRowAssignmentType = (truck: AssetInfo): string => {
    const staged = stagedEdits[truck.symbotId]?.assignmentType;
    if (staged) return staged;
    if (mode === 'manual') {
      const assignment = assignmentsByTruckBumper[truck.bumperNumber];
      if (!assignment) return '';
      const fulfilled = fulfilledOverrideValues[truck.symbotId];
      const currentActive =
        assignment.overrideNode != null &&
        assignment.overrideNode !== fulfilled?.current;
      const nextActive =
        assignment.nextOverrideNode != null &&
        assignment.nextOverrideNode !== fulfilled?.next;
      const hasActiveOverride =
        (assignment.hasActiveOverride ?? true) && (currentActive || nextActive);
      if (hasActiveOverride) return 'manual';
      return assignment.assignmentType === 'locked' ? 'locked' : 'shovel';
    }
    return getRowShovelLock(truck) ? 'locked' : '';
  };

  const truckSortGetters: SortValueGetters<AssetInfo> = {
    name: (truck) => truck.bumperNumber,
    equipmentType: (truck) =>
      truck.makeModelDetails
        ? `${truck.makeModelDetails.make} ${truck.makeModelDetails.model}`
        : null,
    operator: (truck) => {
      const name = lastStatuses[truck.symbotId]?.operatorName;
      return name && name.toLowerCase() !== 'system' ? name : null;
    },
    status: (truck) => lastStatuses[truck.symbotId]?.payload?.status ?? null,
    activity: (truck) => {
      const st = lastStatuses[truck.symbotId];
      if (!isReadyStatus(st?.payload?.status)) return null;
      return resolveReasonDisplay(
        st?.payload?.status,
        st?.payload?.reason,
        st?.payload?.description
      );
    },
    shovelLock: (truck) =>
      stagedEdits[truck.symbotId]?.shovelLock ?? getRowShovelLock(truck),
    regionLock: (truck) => {
      const regionLock =
        mode === 'manual'
          ? assignmentsByTruckBumper[truck.bumperNumber]?.dumpingNode ?? null
          : (truck.meta?.regionLock as string | undefined) ?? null;
      return stagedEdits[truck.symbotId]?.regionLock ?? regionLock ?? null;
    },
    tonnage: (truck) => {
      const st = lastStatuses[truck.symbotId];
      const truckActivity = (st?.payload?.reason ?? '').trim().toLowerCase();
      const current = getEffective(truck.symbotId, 'currentAssignment');
      if (!current && !getRowShovelLock(truck)) return null;
      const isHauling =
        truckActivity === 'traveling'
          ? false
          : truckActivity === 'hauling'
          ? true
          : current
          ? resolveNodeType(
              locationTypeMap.get(current.trim().toLowerCase()) ?? ''
            ) !== 'loading'
          : true;
      if (!isHauling) return 0;
      const material = resolveTruckMaterial(truck);
      const categoryTonnage = getMaterialCategoryTonnage(truck, material);
      return sseTonnage[truck.symbotId] ?? categoryTonnage;
    },
    material: (truck) => resolveTruckMaterial(truck),
    currentAssignment: (truck) => {
      const value = getEffective(truck.symbotId, 'currentAssignment');
      if (!value) return null;
      return value;
    },
    assignmentType: (truck) => {
      const value = getRowAssignmentType(truck);
      if (!value) return null;
      return value;
    },
    nextAssignment: (truck) => {
      const value = getEffective(truck.symbotId, 'nextAssignment');
      if (!value) return null;
      return value;
    },
  };

  const {
    sortedRows: sortedHaulTrucks,
    sortKey,
    direction,
    toggleSort,
  } = useSortableRows(haulTrucks, truckSortGetters);

  // resolves through the asset registry's bumperNumber (the same identity
  // shown in the "Name" column) rather than trusting whatever `asset` string
  // a given SSE payload happens to carry (that's the tablet's hardware name,
  // e.g. "Symbot6D", not the truck's identity).
  const deviceLabelsBySymbotId = useMemo(() => {
    const map = new Map<string, string>();
    assets.forEach((a) => {
      map.set(a.symbotId, a.bumperNumber);
    });
    return map;
  }, [assets]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        p: 3,
        pb: 6,
        backgroundColor: bg,
        height: 'calc(100vh - 160px)',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary }}>
          {t('haulers_monitor_title')}
        </Typography>
        {!loading && (
          <Typography fontSize={12} sx={{ color: textMuted, ml: 1.5 }}>
            {haulTrucks.length} {t('hm_trucks_count')}
          </Typography>
        )}
        {canViewSseLogs && (
          <Box sx={{ ml: 1.5 }}>
            <SseLogsViewer
              logs={sseLogs}
              onClear={() => {
                pendingSseLogsRef.current = [];
                setSseLogs([]);
              }}
              deviceLabels={deviceLabelsBySymbotId}
            />
          </Box>
        )}
        <Tooltip title={t('hm_btn_refresh')}>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton
              onClick={() => {
                void handleRefresh();
              }}
              disabled={isRefreshing || loading}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: (th) => alpha(th.palette.divider, 0.6),
                borderRadius: '8px',
                p: 0.8,
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  transition: 'transform 0.5s',
                  transform: isRefreshing ? 'rotate(360deg)' : 'none',
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            isOverriding ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SendIcon fontSize="small" />
            )
          }
          disabled={
            mode === 'manual'
              ? !hasDirty || isOverriding
              : hasDirty || !hasPendingOverride || isOverriding
          }
          onClick={() => {
            if (mode === 'manual') {
              void commitManualPlan();
            } else {
              void overridePlan();
            }
          }}
          sx={{ ml: 1, fontWeight: 700, textTransform: 'none' }}
        >
          {t('hm_override_plan')}
        </Button>
        {mode === 'auto' && (
          <Button
            variant="contained"
            size="small"
            startIcon={
              isSavingAll ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveIcon fontSize="small" />
              )
            }
            disabled={!hasDirty || isSavingAll}
            onClick={() => {
              void saveAll();
            }}
            sx={{ ml: 1, fontWeight: 700, textTransform: 'none' }}
          >
            {t('hm_save')}
          </Button>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            color: textMuted,
            py: 4,
          }}
        >
          <CircularProgress size={18} />
          <Typography fontSize={13}>{t('hm_loading')}</Typography>
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <Box
          sx={{
            bgcolor: panelBg,
            border: `1px solid ${borderColor}`,
            borderRadius: 2,
            position: 'relative',
          }}
        >
          {/* Scroll hint overlay */}
          {canScrollRight && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 72,
                  pointerEvents: 'none',
                  zIndex: 2,
                  borderRadius: '0 8px 8px 0',
                  background: `linear-gradient(to right, transparent, ${panelBg} 80%)`,
                }}
              />
              <Tooltip title={t('dpv2_scroll_hint')} placement="left" arrow>
                <Box
                  onClick={() => {
                    if (tableScrollRef.current) {
                      tableScrollRef.current.scrollTo({
                        left: tableScrollRef.current.scrollWidth,
                        behavior: 'smooth',
                      });
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 3,
                    px: 0.75,
                    py: 0.3,
                    borderRadius: '999px',
                    backgroundColor: alpha(theme.palette.primary.main, 0.18),
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.35
                    )}`,
                    fontSize: 11,
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    userSelect: 'none',
                    lineHeight: 1.4,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.28),
                    },
                  }}
                >
                  ›››
                </Box>
              </Tooltip>
            </>
          )}

          <Box ref={tableScrollRef} sx={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12.5,
              }}
            >
              <thead>
                <tr>
                  {/* Sticky Name column */}
                  <SortableTh
                    label={t('hm_col_name')}
                    sortKey="name"
                    activeSortKey={sortKey}
                    direction={direction}
                    onSort={toggleSort}
                    style={{
                      ...thBase,
                      backgroundColor: stickyBg,
                      position: 'sticky',
                      left: 0,
                      zIndex: 4,
                      boxShadow: '2px 0 6px -2px rgba(0,0,0,0.15)',
                    }}
                  />
                  {NON_STICKY_COLS.map((col) => (
                    <SortableTh
                      key={col.key}
                      label={col.label}
                      sortKey={col.key}
                      activeSortKey={sortKey}
                      direction={direction}
                      onSort={toggleSort}
                      style={thBase}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {haulTrucks.length === 0 && (
                  <tr>
                    <td
                      colSpan={NON_STICKY_COLS.length + 1}
                      style={{
                        padding: '24px',
                        textAlign: 'center',
                        color: textMuted,
                      }}
                    >
                      {t('hm_no_trucks')}
                    </td>
                  </tr>
                )}
                {sortedHaulTrucks.map((truck) => {
                  const st = lastStatuses[truck.symbotId];
                  const statusRaw = st?.payload?.status ?? null;
                  // down-status trucks are no longer dimmed or blocked from
                  // editing (fields below stay enabled regardless of status) —
                  // kept commented instead of removed in case this needs to
                  // come back
                  // const isDown = statusRaw?.toLowerCase() === 'down';
                  const statusBg = statusRaw
                    ? statusGroups.get(statusRaw.toLowerCase())?.colorCode ??
                      STATUS_COLORS[statusRaw.toLowerCase()] ??
                      theme.palette.action.selected
                    : null;
                  // manual mode's locked-trucks config lives on the manual-dispatch
                  // plan (activeAssignments), not the excavator's asset meta anymore —
                  // shovelLockMap (meta.lockedTrucks) is legacy/auto-mode-only data
                  const shovelLock =
                    mode === 'manual'
                      ? assignmentsByTruckBumper[truck.bumperNumber]
                          ?.excavatorBumper ?? null
                      : shovelLockMap[truck.bumperNumber] ?? null;
                  const regionLock =
                    mode === 'manual'
                      ? assignmentsByTruckBumper[truck.bumperNumber]
                          ?.dumpingNode ?? null
                      : (truck.meta?.regionLock as string | undefined) ?? null;
                  // staged edit wins over the server/plan value — what the
                  // dispatcher currently sees in the Shovel/Region Lock cells
                  const effectiveShovelLock =
                    stagedEdits[truck.symbotId]?.shovelLock ?? shovelLock ?? '';
                  const effectiveRegionLock =
                    stagedEdits[truck.symbotId]?.regionLock ?? regionLock ?? '';
                  // hard lock is configured per-excavator on the excavator screen —
                  // once set, trucks locked to it can't be reassigned from here
                  const isHardLocked = shovelLock
                    ? hardLockedExcavatorBumpers.has(shovelLock)
                    : false;
                  const equipmentType = truck.makeModelDetails
                    ? `${truck.makeModelDetails.make} ${truck.makeModelDetails.model}`
                    : '—';
                  const operatorName =
                    st?.operatorName &&
                    st.operatorName.toLowerCase() !== 'system'
                      ? st.operatorName
                      : '—';
                  const operatorLoggedOut = st?.operatorLoggedOut ?? false;
                  // real-time activity (same source as the Activity column) —
                  // the single source of truth for hauling/traveling/loading/
                  // dumping, rather than inferring it from a location's type
                  const truckActivity = (st?.payload?.reason ?? '')
                    .trim()
                    .toLowerCase();

                  return (
                    <tr
                      key={truck.symbotId}
                      // style={{ opacity: isDown ? 0.55 : 1 }}
                    >
                      {/* Name — sticky */}
                      <td
                        style={{
                          ...tdBase,
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          backgroundColor: stickyBg,
                          boxShadow: '2px 0 6px -2px rgba(0,0,0,0.15)',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            fontWeight={700}
                            fontSize={13}
                            sx={{ color: textPrimary }}
                          >
                            {truck.bumperNumber}
                          </Typography>
                          {statusRaw?.toLowerCase() === 'down' &&
                            !initialDownAtLoad.current.has(truck.symbotId) &&
                            !confirmedDown.has(truck.symbotId) &&
                            !userSetDown.has(truck.symbotId) && (
                              <Tooltip
                                title={t('dpv2_down_confirm_tooltip')}
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setDownConfirmModal(truck);
                                  }}
                                  sx={{
                                    p: 0.25,
                                    color: '#ef4444',
                                    '@keyframes hm-bell': {
                                      '0%, 100%': {
                                        transform: 'scale(1)',
                                        opacity: 1,
                                      },
                                      '50%': {
                                        transform: 'scale(1.25)',
                                        opacity: 0.65,
                                      },
                                    },
                                    animation:
                                      'hm-bell 1.4s ease-in-out infinite',
                                  }}
                                >
                                  <NotificationsActiveOutlinedIcon
                                    sx={{ fontSize: 14 }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                        </Box>
                      </td>

                      {/* Equipment Type */}
                      <td style={tdBase}>{equipmentType}</td>

                      {/* Operator */}
                      <td style={tdBase}>
                        {operatorName === '—' ? (
                          operatorName
                        ) : operatorLoggedOut ? (
                          <Box component="span" sx={{ color: 'error.main' }}>
                            {t('hm_operator_logged_out', { operatorName })}
                          </Box>
                        ) : (
                          operatorName
                        )}
                      </td>

                      {/* Status — click edit icon to update */}
                      <td style={tdBase}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {(() => {
                            const rawKey = statusRaw?.toLowerCase() ?? '';
                            const displayName =
                              rawKey === 'active'
                                ? 'READY'
                                : statusRaw?.toUpperCase() ?? '';
                            const reasonText = resolveReasonDisplay(
                              statusRaw,
                              st?.payload?.reason,
                              st?.payload?.description
                            );
                            const label = statusRaw
                              ? [displayName, reasonText]
                                  .filter(Boolean)
                                  .join(' · ')
                              : '—';
                            return (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  px: statusRaw ? 1 : 0,
                                  py: statusRaw ? 0.4 : 0,
                                  borderRadius: 1.5,
                                  backgroundColor: statusBg ?? 'transparent',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: statusRaw ? '#fff' : textMuted,
                                    letterSpacing: '0.02em',
                                  }}
                                >
                                  {label}
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setStatusModal({
                                      truck,
                                      status: statusRaw ?? '',
                                      reason: st?.payload?.reason ?? '',
                                      description:
                                        st?.payload?.description ?? '',
                                    });
                                  }}
                                  sx={{
                                    p: 0.1,
                                    color: statusRaw
                                      ? 'rgba(255,255,255,0.7)'
                                      : textMuted,
                                    '&:hover': {
                                      color: statusRaw
                                        ? '#fff'
                                        : theme.palette.primary.main,
                                    },
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Box>
                            );
                          })()}
                        </Box>
                      </td>

                      {/* Activity — read-only; only meaningful while ready */}
                      <td style={tdBase}>
                        {isReadyStatus(statusRaw)
                          ? resolveReasonDisplay(
                              statusRaw,
                              st?.payload?.reason,
                              st?.payload?.description
                            ) ?? '—'
                          : '—'}
                      </td>

                      {/* Shovel Lock — editable select from excavators */}
                      <td style={tdBase}>
                        <Autocomplete
                          size="small"
                          disabled={isHardLocked /* || isDown */}
                          options={[...excavators].sort((a, b) =>
                            a.bumperNumber.localeCompare(b.bumperNumber)
                          )}
                          getOptionLabel={(exc) => exc.bumperNumber}
                          isOptionEqualToValue={(a, b) =>
                            a.symbotId === b.symbotId
                          }
                          value={
                            excavators.find(
                              (exc) => exc.bumperNumber === effectiveShovelLock
                            ) ?? null
                          }
                          onChange={(_, exc) => {
                            setField(
                              truck.symbotId,
                              'shovelLock',
                              exc?.bumperNumber ?? ''
                            );
                          }}
                          slotProps={{ paper: { sx: { minWidth: 160 } } }}
                          sx={{
                            minWidth: 90,
                            '& .MuiAutocomplete-input': { fontSize: 12 },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                              }}
                            />
                          )}
                        />
                      </td>

                      {/* Region Lock — manual mode reads the truck's actual dumping
                          node off the active plan's assignment; truck.meta.regionLock
                          is legacy/auto-mode-only data and is never written to by the
                          manual-dispatch flow */}
                      <td style={tdBase}>
                        <Autocomplete
                          size="small"
                          disabled={isHardLocked /* || isDown */}
                          options={[...regionLockLocations].sort((a, b) =>
                            a.name.localeCompare(b.name)
                          )}
                          getOptionLabel={(lp) => lp.name}
                          isOptionEqualToValue={(a, b) =>
                            a.locationId === b.locationId
                          }
                          value={
                            regionLockLocations.find(
                              (lp) => lp.name === effectiveRegionLock
                            ) ?? null
                          }
                          onChange={(_, lp) => {
                            setField(
                              truck.symbotId,
                              'regionLock',
                              lp?.name ?? ''
                            );
                          }}
                          slotProps={{ paper: { sx: { minWidth: 160 } } }}
                          sx={{
                            minWidth: 90,
                            '& .MuiAutocomplete-input': { fontSize: 12 },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                              }}
                            />
                          )}
                        />
                      </td>

                      {/* Tonnage — rotates with truck movement: 0 while
                          traveling empty back to load, the resolved tonnage
                          while hauling loaded to dump. Live SSE payload takes
                          first preference over the material-category nominal
                          tonnage. truckActivity (real-time, same source as
                          the Activity column) is the source of truth for
                          hauling vs. traveling — rather than show a blank
                          cell for a truck we know is on an active
                          shovel/locked assignment when activity telemetry is
                          missing, assume it's hauling until real telemetry
                          says otherwise. */}
                      <td style={tdBase}>
                        {(() => {
                          const current = getEffective(
                            truck.symbotId,
                            'currentAssignment'
                          );
                          if (!current && !shovelLock) return '—';
                          const isHauling =
                            truckActivity === 'traveling'
                              ? false
                              : truckActivity === 'hauling'
                              ? true
                              : // fallback when activity telemetry is
                              // missing — current now holds the
                              // destination (routeTo), so heading to a
                              // loading point means traveling empty, not
                              // hauling; anything else defaults to hauling
                              current
                              ? resolveNodeType(
                                  locationTypeMap.get(
                                    current.trim().toLowerCase()
                                  ) ?? ''
                                ) !== 'loading'
                              : true;
                          if (!isHauling) return (0).toFixed(1);
                          const material = resolveTruckMaterial(truck);
                          const categoryTonnage = getMaterialCategoryTonnage(
                            truck,
                            material
                          );
                          const tonnage =
                            sseTonnage[truck.symbotId] ?? categoryTonnage;
                          return tonnage != null ? tonnage.toFixed(1) : '—';
                        })()}
                      </td>

                      {/* Material */}
                      <td style={tdBase}>{resolveTruckMaterial(truck)}</td>

                      {/* Current Assignment — select from active locations */}
                      <td style={tdBase}>
                        {(() => {
                          const currentAssignmentOptions = [
                            ...getCurrentAssignmentOptions(
                              getEffective(truck.symbotId, 'nextAssignment'),
                              truckActivity
                            ),
                          ].sort((a, b) =>
                            getAssignmentDisplayName(a.name).localeCompare(
                              getAssignmentDisplayName(b.name)
                            )
                          );
                          return (
                            <Autocomplete
                              size="small"
                              disabled={isHardLocked /* || isDown */}
                              options={currentAssignmentOptions}
                              getOptionLabel={(lp) =>
                                getAssignmentDisplayName(lp.name)
                              }
                              isOptionEqualToValue={(a, b) =>
                                a.locationId === b.locationId
                              }
                              value={
                                currentAssignmentOptions.find(
                                  (lp) =>
                                    lp.name ===
                                    getEffective(
                                      truck.symbotId,
                                      'currentAssignment'
                                    )
                                ) ?? null
                              }
                              onChange={(_, lp) => {
                                setField(
                                  truck.symbotId,
                                  'currentAssignment',
                                  lp?.name ?? ''
                                );
                              }}
                              renderOption={(props, lp) => (
                                <MenuItem
                                  {...props}
                                  key={lp.locationId}
                                  sx={{
                                    fontSize: 12,
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <span>
                                    {getAssignmentDisplayName(lp.name)}
                                  </span>
                                  <span
                                    style={{ fontSize: 10, color: textMuted }}
                                  >
                                    {lp.locationType}
                                  </span>
                                </MenuItem>
                              )}
                              slotProps={{ paper: { sx: { minWidth: 160 } } }}
                              sx={{
                                minWidth: 90,
                                '& .MuiAutocomplete-input': { fontSize: 12 },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="standard"
                                  InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                  }}
                                />
                              )}
                            />
                          );
                        })()}
                      </td>

                      {/* Assignment Type — editable in both modes; auto-shifts
                          to Manual/Locked when the dispatcher edits the
                          corresponding fields (see setField), defaults to the
                          server-derived value once no staged override exists */}
                      <td style={tdBase}>
                        {(() => {
                          const defaultValue = (): string => {
                            if (mode === 'manual') {
                              const assignment =
                                assignmentsByTruckBumper[truck.bumperNumber];
                              if (!assignment) return '';
                              // hasActiveOverride is the single source of
                              // truth for "Manual" (Backend Update #10) — a
                              // durable 'locked' assignmentType no longer gets
                              // masked by a leftover/expired override slot.
                              // GET /plan/active doesn't always include this
                              // computed field (only PATCH /plan/trucks's own
                              // response reliably does), so derive it from its
                              // own definition — true whenever either override
                              // slot is populated — rather than trust a field
                              // that can be silently missing on refetch.
                              // Also mask out a slot SSE already confirmed
                              // fulfilled, same as getEffective, so this
                              // doesn't keep saying "Manual" while the server
                              // hasn't cleared the field yet.
                              const fulfilled =
                                fulfilledOverrideValues[truck.symbotId];
                              const currentActive =
                                assignment.overrideNode != null &&
                                assignment.overrideNode !== fulfilled?.current;
                              const nextActive =
                                assignment.nextOverrideNode != null &&
                                assignment.nextOverrideNode !== fulfilled?.next;
                              const hasActiveOverride =
                                (assignment.hasActiveOverride ?? true) &&
                                (currentActive || nextActive);
                              if (hasActiveOverride) return 'manual';
                              return assignment.assignmentType === 'locked'
                                ? 'locked'
                                : 'shovel';
                            }
                            return shovelLock ? 'locked' : '';
                          };
                          const assignmentTypeOptions = [
                            ...ASSIGNMENT_TYPE_OPTIONS,
                          ].sort((a, b) =>
                            t(a.labelKey).localeCompare(t(b.labelKey))
                          );
                          const currentValue =
                            stagedEdits[truck.symbotId]?.assignmentType ??
                            defaultValue();
                          return (
                            <Autocomplete
                              size="small"
                              disabled={isHardLocked /* || isDown */}
                              options={assignmentTypeOptions}
                              getOptionLabel={(o) => t(o.labelKey)}
                              isOptionEqualToValue={(a, b) =>
                                a.value === b.value
                              }
                              value={
                                assignmentTypeOptions.find(
                                  (o) => o.value === currentValue
                                ) ?? null
                              }
                              onChange={(_, o) => {
                                const value = o?.value ?? '';
                                // explicitly re-picking Shovel/Locked
                                // Assignment restages shovel+region from the
                                // row's current effective values (not sent
                                // anywhere yet — Override Plan commits it,
                                // Backend Update #10). setField's shovelLock
                                // branch already auto-fills region from the
                                // excavator's own default and sets
                                // assignmentType='shovel'; regionLock's own
                                // branch then overrides both to 'locked' with
                                // the actual effective region
                                if (mode === 'manual' && value === 'shovel') {
                                  setField(
                                    truck.symbotId,
                                    'shovelLock',
                                    effectiveShovelLock
                                  );
                                } else if (
                                  mode === 'manual' &&
                                  value === 'locked'
                                ) {
                                  setField(
                                    truck.symbotId,
                                    'shovelLock',
                                    effectiveShovelLock
                                  );
                                  setField(
                                    truck.symbotId,
                                    'regionLock',
                                    effectiveRegionLock
                                  );
                                } else {
                                  setField(
                                    truck.symbotId,
                                    'assignmentType',
                                    value
                                  );
                                }
                              }}
                              slotProps={{ paper: { sx: { minWidth: 160 } } }}
                              sx={{
                                minWidth: 90,
                                '& .MuiAutocomplete-input': { fontSize: 12 },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="standard"
                                  InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                  }}
                                />
                              )}
                            />
                          );
                        })()}
                      </td>

                      {/* Next Assignment — select from active locations */}
                      <td style={tdBase}>
                        {(() => {
                          const nextAssignmentOptions = [
                            ...getNextAssignmentOptions(
                              getEffective(truck.symbotId, 'currentAssignment'),
                              truckActivity
                            ),
                          ].sort((a, b) =>
                            getAssignmentDisplayName(a.name).localeCompare(
                              getAssignmentDisplayName(b.name)
                            )
                          );
                          return (
                            <Autocomplete
                              size="small"
                              disabled={isHardLocked /* || isDown */}
                              options={nextAssignmentOptions}
                              getOptionLabel={(lp) =>
                                getAssignmentDisplayName(lp.name)
                              }
                              isOptionEqualToValue={(a, b) =>
                                a.locationId === b.locationId
                              }
                              value={
                                nextAssignmentOptions.find(
                                  (lp) =>
                                    lp.name ===
                                    getEffective(
                                      truck.symbotId,
                                      'nextAssignment'
                                    )
                                ) ?? null
                              }
                              onChange={(_, lp) => {
                                setField(
                                  truck.symbotId,
                                  'nextAssignment',
                                  lp?.name ?? ''
                                );
                              }}
                              renderOption={(props, lp) => (
                                <MenuItem
                                  {...props}
                                  key={lp.locationId}
                                  sx={{
                                    fontSize: 12,
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <span>
                                    {getAssignmentDisplayName(lp.name)}
                                  </span>
                                  <span
                                    style={{ fontSize: 10, color: textMuted }}
                                  >
                                    {lp.locationType}
                                  </span>
                                </MenuItem>
                              )}
                              slotProps={{ paper: { sx: { minWidth: 160 } } }}
                              sx={{
                                minWidth: 90,
                                '& .MuiAutocomplete-input': { fontSize: 12 },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="standard"
                                  InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                  }}
                                />
                              )}
                            />
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Down confirmation dialog */}
      <Dialog
        open={Boolean(downConfirmModal)}
        onClose={() => {
          setDownConfirmModal(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          {t('dpv2_down_confirm_title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('dpv2_down_confirm_body', {
              bumper: downConfirmModal?.bumperNumber ?? '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const truck = downConfirmModal;
              setDownConfirmModal(null);
              if (truck) {
                setStatusModal({
                  truck,
                  status:
                    lastStatuses[truck.symbotId]?.payload?.status ?? 'down',
                  reason: lastStatuses[truck.symbotId]?.payload?.reason ?? '',
                  description:
                    lastStatuses[truck.symbotId]?.payload?.description ?? '',
                });
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => {
              if (downConfirmModal) {
                setConfirmedDown((prev) => {
                  const next = new Set(prev);
                  next.add(downConfirmModal.symbotId);
                  return next;
                });
              }
              setDownConfirmModal(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {t('dpv2_down_confirm_yes') || "Yes, it's down"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Edit Modal */}
      <Dialog
        open={Boolean(statusModal)}
        onClose={() => {
          setStatusModal(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          {t('dpv2_edit_status_title')}
          {statusModal ? ` — ${statusModal.truck.bumperNumber}` : ''}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            pt: '16px !important',
          }}
        >
          <Box>
            <Typography
              fontSize={10}
              fontWeight={700}
              letterSpacing="0.08em"
              sx={{ color: textMuted, textTransform: 'uppercase', mb: 1 }}
            >
              {t('dpv2_edit_status_label')}
            </Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}
            >
              {Array.from(statusGroups.entries()).map(
                ([key, { colorCode }]) => {
                  const isSelected = statusModal?.status?.toLowerCase() === key;
                  return (
                    <Box
                      key={key}
                      onClick={() => {
                        if (statusModal) {
                          const firstReason =
                            statusGroups.get(key)?.reasons[0] ?? '';
                          setStatusModal({
                            ...statusModal,
                            status: key,
                            reason: firstReason,
                            description:
                              getDescriptionOptions(key, firstReason)[0] ?? '',
                          });
                        }
                      }}
                      sx={{
                        backgroundColor: colorCode,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 14,
                        textAlign: 'center',
                        py: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        outline: isSelected
                          ? '2.5px solid #fff'
                          : '2.5px solid transparent',
                        outlineOffset: '2px',
                        opacity: isSelected ? 1 : 0.72,
                        transition: 'opacity 0.15s, outline 0.15s',
                        userSelect: 'none',
                        textTransform: 'capitalize',
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      {key}
                    </Box>
                  );
                }
              )}
            </Box>
          </Box>

          {statusModal?.status &&
            (
              statusGroups.get(statusModal.status?.toLowerCase() ?? '')
                ?.reasons ?? []
            ).length > 0 && (
              <Box>
                <Typography
                  fontSize={10}
                  fontWeight={700}
                  letterSpacing="0.08em"
                  sx={{ color: textMuted, textTransform: 'uppercase', mb: 1 }}
                >
                  {t('dpv2_edit_reason_label')}
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={statusModal.reason}
                  onChange={(e) => {
                    const nextReason = e.target.value;
                    setStatusModal({
                      ...statusModal,
                      reason: nextReason,
                      description:
                        getDescriptionOptions(
                          statusModal.status,
                          nextReason
                        )[0] ?? '',
                    });
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em style={{ color: 'inherit', opacity: 0.5 }}>
                      {t('dpv2_reason_placeholder')}
                    </em>
                  </MenuItem>
                  {(
                    statusGroups.get(statusModal.status?.toLowerCase() ?? '')
                      ?.reasons ?? []
                  ).map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

          {statusModal?.status && statusModal.reason && (
            <Box>
              <Typography
                fontSize={10}
                fontWeight={700}
                letterSpacing="0.08em"
                sx={{ color: textMuted, textTransform: 'uppercase', mb: 1 }}
              >
                {t('hm_edit_description_label')}
              </Typography>
              <Autocomplete
                freeSolo
                size="small"
                options={getDescriptionOptions(
                  statusModal.status,
                  statusModal.reason
                )}
                value={statusModal.description}
                onChange={(_e, value) => {
                  setStatusModal({
                    ...statusModal,
                    description: value ?? '',
                  });
                }}
                onInputChange={(_e, value) => {
                  setStatusModal({ ...statusModal, description: value });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('hm_description_placeholder')}
                  />
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={statusSaving}
            onClick={() => {
              setStatusModal(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={statusSaving || !statusModal?.status}
            onClick={() => {
              void saveStatus();
            }}
            startIcon={
              statusSaving ? (
                <CircularProgress size={13} color="inherit" />
              ) : null
            }
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {t('dpv2_edit_status_save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual-mode add-truck-to-plan fallback picker (zero-siblings case) */}
      <Dialog
        open={Boolean(addAssignmentPicker)}
        onClose={() => {
          setAddAssignmentPicker(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('hm_add_assignment_title')}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '16px !important',
          }}
        >
          <Select
            displayEmpty
            value={addAssignmentPicker?.dumpingZoneId ?? ''}
            onChange={(e) => {
              setAddAssignmentPicker((prev) =>
                prev ? { ...prev, dumpingZoneId: e.target.value } : prev
              );
            }}
          >
            <MenuItem value="">
              <em>{t('hm_add_assignment_pick_dump')}</em>
            </MenuItem>
            {activeLocations
              .filter((l) => resolveNodeType(l.locationType) === 'dumping')
              .map((l) => (
                <MenuItem key={l.locationId} value={l.locationId}>
                  {l.name}
                </MenuItem>
              ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddAssignmentPicker(null);
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!addAssignmentPicker?.dumpingZoneId}
            onClick={async () => {
              if (!addAssignmentPicker) return;
              const truck = assets.find(
                (a) => a.symbotId === addAssignmentPicker.symbotId
              );
              if (!truck) return;
              const symbotId = addAssignmentPicker.symbotId;
              try {
                await manualDispatchApi.patchTrucks({
                  assignments: [
                    {
                      truckBumper: truck.bumperNumber,
                      assignmentType: 'locked',
                      excavatorBumper: addAssignmentPicker.excavatorBumper,
                      dumpingZoneId: addAssignmentPicker.dumpingZoneId,
                    },
                  ],
                });
                setStagedEdits((prev) => {
                  const savedEdit = prev[symbotId];
                  if (!savedEdit) return prev;
                  const {
                    shovelLock: _shovelLock,
                    regionLock: _regionLock,
                    assignmentType: _assignmentType,
                    ...rest
                  } = savedEdit;
                  persistManualStagedEdits({ ...prev, [symbotId]: rest });
                  return { ...prev, [symbotId]: rest };
                });
                setDirtyTrucks((prev) => {
                  const next = new Set(prev);
                  next.delete(symbotId);
                  return next;
                });
                setAddAssignmentPicker(null);
                dispatch(
                  showNotification({
                    message: t('hm_override_success'),
                    type: 'success',
                  })
                );
                await refreshActivePlan();
              } catch (err: any) {
                const message =
                  err?.response?.data?.data?.error?.detail ??
                  err?.response?.data?.message ??
                  t('hm_override_error');
                dispatch(showNotification({ message, type: 'error' }));
              }
            }}
          >
            {t('hm_add_assignment_confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Export ───────────────────────────────────────────────────────────────────

const HaulersMonitorView: React.FC = () => (
  <CustomMuiThemeProvider>
    <HaulersMonitorInner />
  </CustomMuiThemeProvider>
);

export default HaulersMonitorView;
